import React, { useEffect, useState, useRef, Fragment, createElement } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { ConsultationTypeBadge } from '../../components/ui/ConsultationTypeBadge';
import { ClinicianServiceCards } from './ClinicianServiceCards';
import { Encounter, Patient, ClinicalNote, LabRequest, RadiologyRequest, Prescription } from '../../types';
import { Stethoscope, FileText, FlaskConical, Scan, Pill, CheckCircle, History, X, ZoomIn, ZoomOut, Eye, Printer, Download, Trash2, RotateCcw } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { SickNoteForm } from './SickNoteForm';
/**
 * Capture a DOM element and export it as a properly paginated A4 PDF.
 * A4 layout: 210mm × 297mm, margins 20mm top/bottom, 18mm left/right.
 */
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  const PAGE_W_MM = 210;
  const PAGE_H_MM = 297;
  const MARGIN_TOP_MM = 20;
  const MARGIN_BOTTOM_MM = 20;
  const MARGIN_LEFT_MM = 18;
  const MARGIN_RIGHT_MM = 18;
  const CONTENT_W_MM = PAGE_W_MM - MARGIN_LEFT_MM - MARGIN_RIGHT_MM;
  const CONTENT_H_MM = PAGE_H_MM - MARGIN_TOP_MM - MARGIN_BOTTOM_MM;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  const sourceW = sourceCanvas.width;
  const sourceH = sourceCanvas.height;
  const pxPerMm = sourceW / CONTENT_W_MM;
  const pageSlicePx = Math.floor(CONTENT_H_MM * pxPerMm);
  let cursorPx = 0;
  let pageIndex = 0;
  while (cursorPx < sourceH) {
    const remainingPx = sourceH - cursorPx;
    const sliceHpx = Math.min(pageSlicePx, remainingPx);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = sourceW;
    sliceCanvas.height = sliceHpx;
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) break;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sourceW, sliceHpx);
    ctx.drawImage(sourceCanvas, 0, cursorPx, sourceW, sliceHpx, 0, 0, sourceW, sliceHpx);
    const sliceImg = sliceCanvas.toDataURL('image/jpeg', 0.95);
    const sliceHmm = sliceHpx / pxPerMm;
    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(sliceImg, 'JPEG', MARGIN_LEFT_MM, MARGIN_TOP_MM, CONTENT_W_MM, sliceHmm);
    cursorPx += sliceHpx;
    pageIndex += 1;
  }
  pdf.save(filename);
}
import { fbcStructure, urinalysisTests, testosteroneTests, hormonalProfileTests, amhStructure, isAMHTest, hcgStructure, isHCGTest, isHCGAbnormal, isFBCTest, isUrinalysisTest, isTestosteroneTest, isHormonalProfileTest } from '../../store/labStructures';
type CViewingTest = {
  kind: 'lab';
  labId: string;
  testName?: string;
} | {
  kind: 'rad';
  radId: string;
} | null;
export const ClinicianDashboard: React.FC = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser,
    getPatientEncounters,
    logAudit
  } = useData();
  const [activeTab, setActiveTab] = useState<'queue' | 'completed'>('queue');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEncounterId, setSelectedEncounterId] = useState<string | null>(null);
  // Live encounter - always reads from context so lab/rad updates appear instantly
  const selectedEncounter = selectedEncounterId ? encounters.find(e => e.id === selectedEncounterId) || null : null;
  // Image zoom state
  const [zoomImage, setZoomImage] = useState<string | null>(null);
  // View Test File modal state
  const [viewingTest, setViewingTest] = useState<CViewingTest>(null);
  const [testImageZoom, setTestImageZoom] = useState<number>(100);
  const [isGeneratingTestPdf, setIsGeneratingTestPdf] = useState(false);
  const testModalRef = useRef<HTMLDivElement>(null);
  // Remove Test Modal state
  const [removeTestModal, setRemoveTestModal] = useState<{
    isOpen: boolean;
    labId: string;
    testName: string;
    reason: string;
  }>({
    isOpen: false,
    labId: '',
    testName: '',
    reason: ''
  });
  const closeTestModal = () => {
    setViewingTest(null);
    setTestImageZoom(100);
  };
  const handlePrintTestModal = () => {
    document.body.classList.add('printing-test-modal');
    window.print();
    setTimeout(() => {
      document.body.classList.remove('printing-test-modal');
    }, 500);
  };
  const handleDownloadTestPdf = async () => {
    if (!testModalRef.current) return;
    setIsGeneratingTestPdf(true);
    try {
      const filename = `BMED_TestResult_${Date.now()}.pdf`;
      await captureToA4Pdf(testModalRef.current, filename);
    } catch (err) {
      console.error('Test PDF failed', err);
      alert('Failed to generate PDF.');
    } finally {
      setIsGeneratingTestPdf(false);
    }
  };
  // Modals state
  const [showLabModal, setShowLabModal] = useState(false);
  const [showRadModal, setShowRadModal] = useState(false);
  const [showRxModal, setShowRxModal] = useState(false);
  const [showSickNoteModal, setShowSickNoteModal] = useState(false);
  // Clinical Notes State
  const [notes, setNotes] = useState<Partial<ClinicalNote>>({
    presentingComplaint: '',
    historyOfPresentingComplaint: '',
    pastMedicalHistory: [],
    drugHistory: '',
    familyMedicalHistory: [],
    obgynHistory: '',
    socialHistory: {
      alcohol: '',
      smoking: '',
      occupation: '',
      mentalStatus: ''
    },
    reviewOfSystems: {
      rs: '',
      cvs: '',
      cns: '',
      git: '',
      gut: '',
      mss: '',
      is: ''
    },
    onExamination: '',
    onExaminationDetails: {
      gc: '',
      febrile: '',
      pjc: '',
      s1s2: '',
      rs: '',
      pa: ''
    },
    significantFindings: [],
    workingDiagnosis: []
  });
  const [reviewDateNotes, setReviewDateNotes] = useState('');
  // Lab Request State
  const [selectedTests, setSelectedTests] = useState<string[]>([]);
  const [otherTests, setOtherTests] = useState('');
  // Rad Request State
  const [scanType, setScanType] = useState('');
  // Rx State
  const [rxForm, setRxForm] = useState({
    drugName: '',
    dose: '',
    route: '',
    frequency: '',
    days: ''
  });
  // Active queue only. Completed visits live in Patient Files and can be
  // reopened from there via the "Amend File" flow.
  const pendingEncounters = encounters.filter(e => e.stage !== 'COMPLETED');
  const completedEncounters = encounters.filter(e => e.stage === 'COMPLETED');
  const getPatient = (patientId: string) => patients.find(p => p.id === patientId);
  const filteredCompleted = completedEncounters.filter(enc => {
    const p = getPatient(enc.patientId);
    if (!p) return false;
    const q = searchQuery.toLowerCase();
    return p.firstName.toLowerCase().includes(q) || p.lastName.toLowerCase().includes(q) || p.bmcFileNumber.toLowerCase().includes(q);
  }).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  const handleSelectEncounter = (encounter: Encounter) => {
    setSelectedEncounterId(encounter.id);
    setReviewDateNotes(encounter.reviewDateNotes || '');
    if (encounter.clinicalNote) {
      setNotes(encounter.clinicalNote);
    } else {
      setNotes({
        presentingComplaint: '',
        historyOfPresentingComplaint: '',
        pastMedicalHistory: [],
        drugHistory: '',
        familyMedicalHistory: [],
        obgynHistory: '',
        socialHistory: {
          alcohol: '',
          smoking: '',
          occupation: '',
          mentalStatus: ''
        },
        reviewOfSystems: {
          rs: '',
          cvs: '',
          cns: '',
          git: '',
          gut: '',
          mss: '',
          is: ''
        },
        onExamination: '',
        onExaminationDetails: {
          gc: '',
          febrile: '',
          pjc: '',
          s1s2: '',
          rs: '',
          pa: ''
        },
        significantFindings: [],
        workingDiagnosis: []
      });
    }
    // Move encounter back to CLINICIAN stage if returning from investigations
    if (encounter.stage === 'INVESTIGATIONS') {
      updateEncounter(encounter.id, {
        stage: 'CLINICIAN',
        lastAttendingClinician: currentUser?.fullName
      });
    } else if (currentUser && encounter.lastAttendingClinician !== currentUser.fullName) {
      // Record that this clinician opened/consulted the file.
      updateEncounter(encounter.id, {
        lastAttendingClinician: currentUser.fullName
      });
    }
  };
  const handleSaveNotes = () => {
    if (!selectedEncounter || !currentUser) return;
    const isAmending = selectedEncounter.stage === 'COMPLETED';
    let editHistory = selectedEncounter.clinicalNote?.editHistory || [];
    if (isAmending) {
      const reason = prompt('Please enter a mandatory reason for modifying this completed consultation:');
      if (!reason || !reason.trim()) {
        alert('A reason is required to amend a completed consultation.');
        return;
      }
      const previousNote = selectedEncounter.clinicalNote!;
      const {
        editHistory: _oldHistory,
        ...noteSnapshot
      } = previousNote;
      editHistory = [...editHistory, {
        editedAt: new Date().toISOString(),
        editedBy: currentUser.id,
        editedByName: currentUser.fullName,
        reason: reason.trim(),
        previousNote: noteSnapshot as any
      }];
      logAudit({
        type: 'consultation.edit',
        encounterId: selectedEncounter.id,
        patientId: selectedEncounter.patientId,
        patientName: `${getPatient(selectedEncounter.patientId)?.firstName} ${getPatient(selectedEncounter.patientId)?.lastName}`,
        userId: currentUser.id,
        userName: currentUser.fullName,
        userRole: currentUser.role,
        reason: reason.trim(),
        previousContent: JSON.stringify(noteSnapshot),
        newContent: JSON.stringify(notes)
      });
    }
    const clinicalNote: ClinicalNote = {
      ...(notes as ClinicalNote),
      recordedAt: isAmending ? selectedEncounter.clinicalNote!.recordedAt : new Date().toISOString(),
      recordedBy: isAmending ? selectedEncounter.clinicalNote!.recordedBy : currentUser.id,
      clinicianName: isAmending ? selectedEncounter.clinicalNote!.clinicianName : currentUser.fullName,
      editHistory
    };
    updateEncounter(selectedEncounter.id, {
      clinicalNote,
      reviewDateNotes,
      lastAttendingClinician: currentUser.fullName
    });
    alert(isAmending ? 'Amendment saved and logged successfully.' : 'Clinical notes saved successfully.');
  };
  const handleRequestLab = () => {
    if (!selectedEncounter || !currentUser || selectedTests.length === 0 && !otherTests) return;
    const newLab: LabRequest = {
      id: `lab-${Date.now()}`,
      tests: selectedTests,
      otherTests,
      requestedAt: new Date().toISOString(),
      requestedBy: currentUser.id,
      status: 'Pending'
    };
    updateEncounter(selectedEncounter.id, {
      labRequests: [...selectedEncounter.labRequests, newLab],
      stage: 'INVESTIGATIONS',
      pendingLab: true
    });
    setShowLabModal(false);
    setSelectedTests([]);
    setOtherTests('');
    // File stays open — clinician closes/saves/completes when ready.
    alert('Laboratory tests requested. The patient file remains open — you can continue your consultation.');
  };
  const handleRequestRad = () => {
    if (!selectedEncounter || !currentUser || !scanType) return;
    const newRad: RadiologyRequest = {
      id: `rad-${Date.now()}`,
      scanType,
      requestedAt: new Date().toISOString(),
      requestedBy: currentUser.id,
      status: 'Pending'
    };
    updateEncounter(selectedEncounter.id, {
      radiologyRequests: [...selectedEncounter.radiologyRequests, newRad],
      stage: 'INVESTIGATIONS',
      pendingRadiology: true
    });
    setShowRadModal(false);
    setScanType('');
    // File stays open — clinician closes/saves/completes when ready.
    alert('Radiology scan requested. The patient file remains open — you can continue your consultation.');
  };
  const handleAddRx = () => {
    if (!selectedEncounter || !currentUser || !rxForm.drugName) return;
    if (selectedEncounter.prescriptions.length >= 20) {
      alert('You have reached the maximum of 20 drugs per prescription. Remove an existing item before adding another.');
      return;
    }
    const newRx: Prescription = {
      id: `rx-${Date.now()}`,
      ...rxForm,
      status: 'Pending',
      prescribedAt: new Date().toISOString(),
      prescribedBy: currentUser.id
    };
    updateEncounter(selectedEncounter.id, {
      prescriptions: [...selectedEncounter.prescriptions, newRx]
    });
    setRxForm({
      drugName: '',
      dose: '',
      route: '',
      frequency: '',
      days: ''
    });
  };
  // Delete a single prescription. Only allowed while it is still Pending —
  // once Pharmacy has dispensed (or marked unavailable), it stays on record.
  const handleDeletePrescription = (rxId: string) => {
    if (!selectedEncounter) return;
    const rx = selectedEncounter.prescriptions.find(p => p.id === rxId);
    if (!rx) return;
    if (rx.status !== 'Pending') {
      alert('This drug has already been processed by Pharmacy and cannot be removed from the record.');
      return;
    }
    if (!confirm(`Remove "${rx.drugName}" from the prescription list? This cannot be undone.`)) return;
    updateEncounter(selectedEncounter.id, {
      prescriptions: selectedEncounter.prescriptions.filter(p => p.id !== rxId)
    });
  };
  // Recall (remove) a single ordered lab test that has not yet been completed.
  // - For tests inside lab.tests[]: drop just that test from the array.
  // - If that empties the request (and no otherTests), remove the whole request.
  // - For an "other test" entry: clear lab.otherTests on that request, or
  //   remove the request entirely if it has no tests left either.
  const handleRecallLabTest = (labId: string, testName: string, isOther = false) => {
    if (!selectedEncounter) return;
    const lab = selectedEncounter.labRequests.find(l => l.id === labId);
    if (!lab) return;
    if (lab.status === 'Completed') {
      alert('This test has already been completed by the laboratory and cannot be recalled.');
      return;
    }
    if (!confirm(`Recall the lab test "${testName}"? The laboratory will no longer see this request.`)) return;
    let updatedLabs = selectedEncounter.labRequests.map(l => {
      if (l.id !== labId) return l;
      if (isOther) return {
        ...l,
        otherTests: ''
      };
      return {
        ...l,
        tests: l.tests.filter(t => t !== testName)
      };
    });
    // Drop any request that now has no tests AND no otherTests.
    updatedLabs = updatedLabs.filter(l => l.tests.length > 0 || l.otherTests.trim().length > 0);
    const stillPendingLab = updatedLabs.some(l => l.status === 'Pending');
    updateEncounter(selectedEncounter.id, {
      labRequests: updatedLabs,
      pendingLab: stillPendingLab
    });
  };
  // Recall (remove) a pending radiology request.
  const handleRecallRadiology = (radId: string) => {
    if (!selectedEncounter) return;
    const rad = selectedEncounter.radiologyRequests.find(r => r.id === radId);
    if (!rad) return;
    if (rad.status === 'Completed') {
      alert('This scan has already been completed by radiology and cannot be recalled.');
      return;
    }
    if (!confirm(`Recall the radiology request "${rad.scanType}"? Radiology will no longer see this request.`)) return;
    const updatedRads = selectedEncounter.radiologyRequests.filter(r => r.id !== radId);
    const stillPendingRad = updatedRads.some(r => r.status === 'Pending');
    updateEncounter(selectedEncounter.id, {
      radiologyRequests: updatedRads,
      pendingRadiology: stillPendingRad
    });
  };
  const handleRemoveTestConfirm = () => {
    if (!selectedEncounter || !currentUser || !removeTestModal.reason.trim()) return;
    const lab = selectedEncounter.labRequests.find(l => l.id === removeTestModal.labId);
    if (!lab) return;
    const now = new Date().toISOString();
    const newEntry = {
      testName: removeTestModal.testName,
      removedAt: now,
      removedBy: currentUser.id,
      reason: removeTestModal.reason
    };
    const updatedLabRequests = selectedEncounter.labRequests.map(l => {
      if (l.id !== removeTestModal.labId) return l;
      return {
        ...l,
        removedTestNames: [...(l.removedTestNames || []), removeTestModal.testName],
        removedTestEntries: [...(l.removedTestEntries || []), newEntry]
      };
    });
    updateEncounter(selectedEncounter.id, {
      labRequests: updatedLabRequests
    });
    const patient = patients.find(p => p.id === selectedEncounter.patientId);
    logAudit({
      type: 'lab.test.remove',
      encounterId: selectedEncounter.id,
      patientId: selectedEncounter.patientId,
      patientName: patient ? `${patient.firstName} ${patient.lastName}` : 'Unknown',
      userId: currentUser.id,
      userName: currentUser.fullName,
      userRole: currentUser.role,
      reason: removeTestModal.reason,
      labRequestId: removeTestModal.labId,
      testName: removeTestModal.testName
    });
    setRemoveTestModal({
      isOpen: false,
      labId: '',
      testName: '',
      reason: ''
    });
  };
  const handleCompleteVisit = () => {
    if (!selectedEncounter || !currentUser) return;
    // Save notes one last time
    const clinicalNote: ClinicalNote = {
      ...(notes as ClinicalNote),
      recordedAt: new Date().toISOString(),
      recordedBy: currentUser.id,
      clinicianName: currentUser.fullName
    };
    const hasPrescriptions = selectedEncounter.prescriptions.length > 0;
    const hasPendingDispense = selectedEncounter.prescriptions.some(rx => rx.status === 'Pending');
    // If prescriptions exist and none have been dispensed yet, send to pharmacy.
    // If pharmacy has already processed them (returned to clinician), complete the visit.
    const sendToPharmacy = hasPrescriptions && hasPendingDispense;
    updateEncounter(selectedEncounter.id, {
      clinicalNote,
      reviewDateNotes,
      lastAttendingClinician: currentUser.fullName,
      stage: sendToPharmacy ? 'PHARMACY' : 'COMPLETED',
      completedAt: sendToPharmacy ? undefined : new Date().toISOString()
    });
    alert(sendToPharmacy ? 'Visit saved. Patient forwarded to Pharmacy.' : 'Visit completed successfully.');
    setSelectedEncounterId(null);
  };
  const handleSendToPharmacy = () => {
    if (!selectedEncounter || !currentUser) return;
    const pendingRx = selectedEncounter.prescriptions.filter(rx => rx.status === 'Pending');
    if (pendingRx.length === 0) {
      alert('No pending prescriptions to send to pharmacy.');
      return;
    }
    // Save current notes
    const clinicalNote: ClinicalNote = {
      ...(notes as ClinicalNote),
      recordedAt: new Date().toISOString(),
      recordedBy: currentUser.id,
      clinicianName: currentUser.fullName
    };
    updateEncounter(selectedEncounter.id, {
      clinicalNote,
      reviewDateNotes,
      lastAttendingClinician: currentUser.fullName,
      stage: 'PHARMACY'
    });
    // File stays open — clinician decides when to close, save draft, or complete the visit.
    alert(`Prescription sent to Pharmacy (${pendingRx.length} drug${pendingRx.length > 1 ? 's' : ''}). The patient file remains open — you can continue your consultation.`);
  };
  if (selectedEncounter) {
    const patient = getPatient(selectedEncounter.patientId);
    if (!patient) return <div data-id="element-2085" data-mp="2ba04pttuyt3w">Patient not found</div>;
    const isCompleted = selectedEncounter.stage === 'COMPLETED';
    const isOriginalClinician = selectedEncounter.clinicalNote?.recordedBy === currentUser?.id;
    const isAdmin = currentUser?.role === 'Administrator';
    const canEdit = !isCompleted || isOriginalClinician || isAdmin;
    return <div className="space-y-6 pb-20" data-id="element-2086" data-mp="1niur7lbe4cwr">
        {isCompleted && !canEdit && <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded flex items-center mb-4" data-id="element-2087" data-mp="4pb2vdr7js15">
            <span className="text-sm font-medium" data-id="element-2088" data-mp="or7aema8wh4">
              This consultation is completed and can only be amended by the
              original clinician (
              {selectedEncounter.clinicalNote?.clinicianName}) or an
              Administrator.
            </span>
          </div>}
        {isCompleted && canEdit && <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded flex items-center mb-4" data-id="element-2089" data-mp="3hxe50wfmfheo">
            <History className="w-5 h-5 mr-2 text-amber-600" data-id="element-2090" data-mp="392q3nhd62jv5" />
            <span className="text-sm font-medium" data-id="element-2091" data-mp="3915a9v2l96yv">
              You are viewing a completed consultation. Any edits saved will be
              logged in the audit trail.
            </span>
          </div>}
        {/* Sticky Action Bar */}
        <div className="flex justify-between items-center sticky top-0 bg-gray-100 z-10 py-4 border-b border-gray-200" data-id="element-2092" data-mp="2u5pbjcr2cmw5">
          <div className="min-w-0" data-id="element-2093" data-mp="2khlg5lf6pdjh">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-2094" data-mp="2kkpo8tvrstff">
              {isCompleted ? 'Completed Consultation' : 'Consultation'}
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-700" data-id="element-2095" data-mp="2biq6u0gufnby">
              <span data-id="element-2096" data-mp="2fkbijihn5vkk">
                <span className="text-gray-500" data-id="element-2097" data-mp="2fjjway9xpa6y">Patient:</span>{' '}
                <span className="font-semibold text-gray-900" data-id="element-2098" data-mp="339w0by226ms9">
                  {patient.firstName} {patient.lastName}
                </span>
              </span>
              <span className="flex items-center gap-2" data-id="element-2099" data-mp="2ugixo4e706sz">
                <span className="text-gray-500" data-id="element-2100" data-mp="2ug14jgmtu8pa">Consultation Type:</span>
                {selectedEncounter.consultationType ? <ConsultationTypeBadge type={selectedEncounter.consultationType} data-id="element-2101" data-mp="2yfrslckyyaq4" /> : <span className="italic text-gray-500" data-id="element-2102" data-mp="3qzgvrgfefcif">Not specified</span>}
              </span>
            </div>
          </div>
          <div className="flex space-x-2" data-id="element-2103" data-mp="1fhdgifcc0dhg">
            <Button variant="outline" onClick={() => setSelectedEncounterId(null)} data-id="element-2104" data-mp="1al52j59veqbv">
              Close File
            </Button>
            <Button variant="outline" className="text-amber-700 border-amber-600 hover:bg-amber-50" onClick={() => {
            const reason = prompt('Send this file back to Nursing so the vitals can be corrected. Please provide a brief reason (optional):', '') ?? '';
            if (confirm('Are you sure you want to send this file back to Nursing? The patient will leave your queue until the nurse re-submits the corrected vitals.')) {
              const notePrefix = reason ? `[Returned to Nursing by ${currentUser?.fullName || 'Clinician'}: ${reason}]` : `[Returned to Nursing by ${currentUser?.fullName || 'Clinician'}]`;
              updateEncounter(selectedEncounter.id, {
                stage: 'NURSING',
                vitalsReturnNote: notePrefix,
                vitalsReturnedAt: new Date().toISOString()
              } as any);
              alert('File sent back to Nursing for vitals correction. It will return to your queue once the nurse re-submits the vitals.');
              setSelectedEncounterId(null);
            }
          }} data-id="element-2105" data-mp="1ka6g6osy0vmo">
              ↩ Send to Nursing
            </Button>
            <Button variant="outline" onClick={() => setShowSickNoteModal(true)} data-id="element-2106" data-mp="1j6pnlz24t0aq">
              <FileText className="w-4 h-4 mr-2" data-id="element-2107" data-mp="1o4zhm6c4duur" /> Generate Sick Note
            </Button>
            <Button variant="primary" onClick={handleSaveNotes} data-id="element-2108" data-mp="1t6ghpzms5daa">
              {selectedEncounter.stage === 'COMPLETED' ? 'Save Amendment' : 'Save Draft'}
            </Button>
            {selectedEncounter.stage !== 'COMPLETED' && <Button variant="success" className="bg-green-600 text-white hover:bg-green-700" onClick={handleCompleteVisit} data-id="element-2109" data-mp="1tcz8626nyrjw">
                <CheckCircle className="w-4 h-4 mr-2" data-id="element-2110" data-mp="2m0v1qy0xs3cg" /> Complete Visit
              </Button>}
          </div>
        </div>

        {showSickNoteModal && <SickNoteForm encounter={selectedEncounter} patient={patient} clinician={currentUser} onClose={() => setShowSickNoteModal(false)} data-id="element-2111" data-mp="2casd1a0ulbjx" />}

        {/* Medical Form */}
        <div className="bg-white border-2 border-gray-300 rounded-lg p-6" data-id="element-2112" data-mp="34s4oi8zpwolz">
          <fieldset disabled={!canEdit} className="group" data-id="element-2113" data-mp="300mkfc2ee7ws">
            {/* ═══ FORM HEADER ═══ */}
            {selectedEncounter.lastAttendingClinician && selectedEncounter.lastAttendingClinician !== currentUser?.fullName && <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded flex items-center" data-id="element-2114" data-mp="13404dmyb493g">
                  <Stethoscope className="w-5 h-5 mr-2 text-blue-600" data-id="element-2115" data-mp="tc43k44u64vj" />
                  <span className="text-sm font-medium" data-id="element-2116" data-mp="ta164z1ct9gs">
                    This file was last attended to by:{' '}
                    <span className="font-bold" data-id="element-2117" data-mp="kbfrvg2x0glt">
                      {selectedEncounter.lastAttendingClinician}
                    </span>
                  </span>
                </div>}
            <div className="flex justify-between items-start mb-6" data-id="element-2118" data-mp="ad9r51suhgij">
              <div className="flex items-center" data-id="element-2119" data-mp="adi4svwskpus">
                <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-20 w-20 object-contain mr-4" data-id="element-2120" data-mp="1zrocdc4nml9a" />
                <div data-id="element-2121" data-mp="3p9v1vambketp">
                  <h1 className="text-2xl font-bold text-pink-600 uppercase tracking-wider" data-id="element-2122" data-mp="3p6yzna3fv3zg">
                    BMED MEDICAL CENTRE
                  </h1>
                  <p className="text-sm italic text-gray-600" data-id="element-2123" data-mp="2usambmjx1g2">
                    Caring Healing Leading
                  </p>
                </div>
              </div>
              <div className="text-right" data-id="element-2124" data-mp="bu5g6t7k15vi">
                <p className="font-bold text-lg uppercase tracking-widest border-b-2 border-black pb-1 mb-2" data-id="element-2125" data-mp="bsu8iiazndz4">
                  Confidential
                </p>
                <p className="text-sm" data-id="element-2126" data-mp="lt4mi54ukpfc">
                  <span className="font-semibold" data-id="element-2127" data-mp="ls323a1xyqky">Date:</span>{' '}
                  {new Date(selectedEncounter.startedAt).toLocaleDateString()}
                </p>
                <p className="text-sm" data-id="element-2128" data-mp="2nj6d40fqu761">
                  <span className="font-semibold" data-id="element-2129" data-mp="2nk7z2zyase2j">File No:</span>{' '}
                  {patient.bmcFileNumber}
                </p>
              </div>
            </div>

            {/* ═══ PERSONAL DETAILS ═══ */}
            <div className="border-2 border-black p-4 mb-6 text-sm space-y-3" data-id="element-2130" data-mp="1wpmo6husxndb">
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-2131" data-mp="21ozcl71bn2i0">
                <div className="col-span-1 font-semibold" data-id="element-2132" data-mp="26itwaagxfhh3">1.</div>
                <div className="col-span-6" data-id="element-2133" data-mp="2bh5126sd71sg">
                  <span className="font-semibold" data-id="element-2134" data-mp="2bftyubeki23g">Full Names:</span>{' '}
                  {patient.firstName} {patient.lastName}
                </div>
                <div className="col-span-3" data-id="element-2135" data-mp="1ireo412gy5xx">
                  <span className="font-semibold" data-id="element-2136" data-mp="1is6jpbw2g3o7">Sex:</span> {patient.sex}
                </div>
                <div className="col-span-2" data-id="element-2137" data-mp="1npg7yo7py9ag">
                  <span className="font-semibold" data-id="element-2138" data-mp="1spugs8h59n9j">Age:</span> {patient.age}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-2139" data-mp="2z6zeapglisd8">
                <div className="col-span-1 font-semibold" data-id="element-2140" data-mp="34510hj9bj7sx">2.</div>
                <div className="col-span-11" data-id="element-2141" data-mp="347dnwbxuiadd">
                  <span className="font-semibold" data-id="element-2142" data-mp="si77ftxzoi6a">Marital Status:</span>{' '}
                  {patient.maritalStatus}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-2143" data-mp="nl70zhx89den">
                <div className="col-span-1 font-semibold" data-id="element-2144" data-mp="iq1g8mfka4s5">3.</div>
                <div className="col-span-7" data-id="element-2145" data-mp="drzuftqr3rcz">
                  <span className="font-semibold" data-id="element-2146" data-mp="dr6wni9rsc6z">Home Address:</span>{' '}
                  {patient.address}
                </div>
                <div className="col-span-4" data-id="element-2147" data-mp="4rtkuiy6bbj3">
                  <span className="font-semibold" data-id="element-2148" data-mp="4r1rles1tpvz">Contact:</span>{' '}
                  {patient.contact}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-2149" data-mp="3r02zbk6jtuu7">
                <div className="col-span-1 font-semibold" data-id="element-2150" data-mp="1u716lcicm9jj">4.</div>
                <div className="col-span-11" data-id="element-2151" data-mp="1u6hwghyxwpg2">
                  <span className="font-semibold" data-id="element-2152" data-mp="1p7omv0mrxil6">Occupation:</span>{' '}
                  {patient.occupation || '_________________________'}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-2153" data-mp="2ajai59czxzg5">
                <div className="col-span-1 font-semibold" data-id="element-2154" data-mp="2ah6dgp79wwet">5.</div>
                <div className="col-span-5" data-id="element-2155" data-mp="2p7dqgpeihn84">
                  <span className="font-semibold" data-id="element-2156" data-mp="2k92g8dkb1apd">Next of Kin:</span>{' '}
                  {patient.nextOfKin}
                </div>
                <div className="col-span-3" data-id="element-2157" data-mp="1wkaspzhntgix">
                  <span className="font-semibold" data-id="element-2158" data-mp="1wl2pveqwnmbb">Contact:</span>{' '}
                  {patient.nokContact}
                </div>
                <div className="col-span-3" data-id="element-2159" data-mp="25kfqrj0yr2kf">
                  <span className="font-semibold" data-id="element-2160" data-mp="25l908rrb4669">Relationship:</span>{' '}
                  {patient.nokRelationship}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-2161" data-mp="3gybgtmteovqf">
                <div className="col-span-1 font-semibold" data-id="element-2162" data-mp="3cxrgkadjdc02">6.</div>
                <div className="col-span-11" data-id="element-2163" data-mp="3czkff3hwqrxi">
                  <span className="font-semibold" data-id="element-2164" data-mp="3kgr0qi8tn7kh">Allergies:</span>{' '}
                  <span className="text-red-600 font-medium" data-id="element-2165" data-mp="3pg3od57ioio4">
                    {patient.allergies}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2" data-id="element-2166" data-mp="3aknnag8irmi8">
                <div className="col-span-1 font-semibold" data-id="element-2167" data-mp="3fh4drh36ycyh">7.</div>
                <div className="col-span-11" data-id="element-2168" data-mp="7246c9vak32q">
                  <span className="font-semibold" data-id="element-2169" data-mp="735lapffh9pq">HIV Status:</span>{' '}
                  <span className="ml-4" data-id="element-2170" data-mp="c1qkcliy1ncl">
                    <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${patient.hivStatus === 'Positive' ? 'bg-red-100 font-bold' : ''}`} data-id="element-2171" data-mp="c0nw6ld09z0d">
                      Positive {patient.hivStatus === 'Positive' ? '✓' : ''}
                    </span>
                    <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${patient.hivStatus === 'Negative' ? 'bg-green-100 font-bold' : ''}`} data-id="element-2172" data-mp="pzni3l94vzsu">
                      Negative {patient.hivStatus === 'Negative' ? '✓' : ''}
                    </span>
                    <span className={`inline-block px-2 py-0.5 border border-black ${patient.hivStatus === 'Unknown' ? 'bg-yellow-100 font-bold' : ''}`} data-id="element-2173" data-mp="zpdqldf0qm59">
                      Unknown {patient.hivStatus === 'Unknown' ? '✓' : ''}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            {/* ═══ NURSING OBSERVATIONS ═══ */}
            <div className="mb-6" data-id="element-2174" data-mp="byzvfe15076g">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-2 border-black border-b-0 text-center" data-id="element-2175" data-mp="c01g8a7yg81d">
                Nursing Observations
              </h3>
              <table className="w-full border-collapse border-2 border-black text-sm text-center" data-id="element-2176" data-mp="2y1xndhakvwb">
                <thead data-id="element-2177" data-mp="3u4weokse6az1">
                  <tr className="bg-gray-50" data-id="element-2178" data-mp="3u5nvuql8s65s">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <th key={n} className="border border-black p-1 text-xs text-gray-500" data-id="element-2179" data-mp="29lsmfkkgtjc1">
                        {n}
                      </th>)}
                  </tr>
                  <tr className="bg-gray-100" data-id="element-2180" data-mp="2nmv8vyfdgvnh">
                    <th className="border border-black p-1" data-id="element-2181" data-mp="2nlk53ylxfnwd">Temp</th>
                    <th className="border border-black p-1" data-id="element-2182" data-mp="1qx5g6yqjynwv">Pulse</th>
                    <th className="border border-black p-1" data-id="element-2183" data-mp="1vuxi8lkwvqes">Resp</th>
                    <th className="border border-black p-1" data-id="element-2184" data-mp="1vt2xb79n3yap">BP</th>
                    <th className="border border-black p-1" data-id="element-2185" data-mp="1zvz84bsxuq43">SpO2</th>
                    <th className="border border-black p-1" data-id="element-2186" data-mp="24o0skl09fn57">Weight (Kg)</th>
                    <th className="border border-black p-1" data-id="element-2187" data-mp="24pbyorh6h6zh">Height</th>
                    <th className="border border-black p-1" data-id="element-2188" data-mp="18534m1srpxth">BMI</th>
                    <th className="border border-black p-1" data-id="element-2189" data-mp="1c6er3ygci2xg">Pain Score</th>
                    <th className="border border-black p-1" data-id="element-2190" data-mp="t2bh94cguiam">AVPU</th>
                  </tr>
                </thead>
                <tbody data-id="element-2191" data-mp="o4068qafq1qt">
                  <tr data-id="element-2192" data-mp="o4t57535jmye">
                    <td className="border border-black p-2 font-medium" data-id="element-2193" data-mp="o1dpq15kgbwg">
                      {selectedEncounter.vitals?.temp || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2194" data-mp="y0msnjiet0uy">
                      {selectedEncounter.vitals?.pulse || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2195" data-mp="16tzu59ujhasn">
                      {selectedEncounter.vitals?.resp || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2196" data-mp="1lqqj0p6l7440">
                      {selectedEncounter.vitals?.bp || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2197" data-mp="3nic3xezz4xfh">
                      {selectedEncounter.vitals?.sp02 || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2198" data-mp="3ildakyo4wuic">
                      {selectedEncounter.vitals?.weight || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2199" data-mp="22zjcfrejl2pi">
                      {selectedEncounter.vitals?.height || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2200" data-mp="1tarqbzw5xcmi">
                      {selectedEncounter.vitals?.bmi || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2201" data-mp="1f9fadoh05b0m">
                      {selectedEncounter.vitals?.painScore || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2202" data-mp="15dc72faez6c3">
                      {selectedEncounter.vitals?.avpu || ''}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* ═══ STAT DOSES ADMINISTERED BY NURSING ═══ */}
            {selectedEncounter.statDoses && selectedEncounter.statDoses.length > 0 && <div className="border-2 border-black mb-6" data-id="element-2203" data-mp="339ssk3crkj9x">
                  <h3 className="font-bold uppercase bg-yellow-100 p-2 border-b border-black text-center text-sm" data-id="element-2204" data-mp="2y9o9q6odvcgj">
                    ⚡ Stat Doses Administered by Nursing
                  </h3>
                  <table className="w-full border-collapse text-sm" data-id="element-2205" data-mp="2tceluvgn78ut">
                    <thead data-id="element-2206" data-mp="2odbhzecef8of">
                      <tr className="bg-gray-100" data-id="element-2207" data-mp="3lz96pthdga8t">
                        <th className="border border-black p-1.5 w-10" data-id="element-2208" data-mp="3lxxs0yme1i2x">No.</th>
                        <th className="border border-black p-1.5 text-left" data-id="element-2209" data-mp="3gzw7e99bdapc">
                          Drug Name
                        </th>
                        <th className="border border-black p-1.5 text-left" data-id="element-2210" data-mp="3c7mp9fefxexm">
                          Dose
                        </th>
                        <th className="border border-black p-1.5 text-left" data-id="element-2211" data-mp="8k2t812ggbbg">
                          Route
                        </th>
                        <th className="border border-black p-1.5 text-left" data-id="element-2212" data-mp="3sui90lh8hw6">
                          Time Given
                        </th>
                        <th className="border border-black p-1.5 text-left" data-id="element-2213" data-mp="c4mry9hb56kk">
                          Nurse
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-2214" data-mp="l51c35ijcfmh">
                      {selectedEncounter.statDoses.map((dose, idx) => <tr key={dose.id} data-id="element-2215" data-mp="pzouqnquyft5" data-mpi={idx}>
                          <td className="border border-black p-1.5 text-center" data-id="element-2216" data-mp="v1xmxhvwpdu7">
                            {idx + 1}
                          </td>
                          <td className="border border-black p-1.5 font-medium" data-id="element-2217" data-mp="z0n2nh4f37h0">
                            {dose.drugName}
                          </td>
                          <td className="border border-black p-1.5" data-id="element-2218" data-mp="18rhnqi5xg10v">
                            {dose.dose}
                          </td>
                          <td className="border border-black p-1.5" data-id="element-2219" data-mp="1dpj7z6f3a6dx">
                            {dose.route}
                          </td>
                          <td className="border border-black p-1.5" data-id="element-2220" data-mp="2415mw2or63uq">
                            {dose.givenAt ? new Date(dose.givenAt).toLocaleString() : ''}
                          </td>
                          <td className="border border-black p-1.5 italic text-xs" data-id="element-2221" data-mp="2d09a9iuyd5f1">
                            {dose.nurseName || ''}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>}

            {/* ═══ CLINICAL NOTES ═══ */}
            <div className="space-y-5 mb-6 text-sm" data-id="element-2222" data-mp="1u40xzv3ivua8">
              {/* PRESENTING COMPLAINT */}
              <div className="border-2 border-black" data-id="element-2223" data-mp="1pc987ms6xsl5">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2224" data-mp="1paod9w1ys7mr">
                  Presenting Complaint
                </h3>
                <textarea className="w-full p-3 min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.presentingComplaint} onChange={e => setNotes({
                ...notes,
                presentingComplaint: e.target.value
              })} data-id="element-2225" data-mp="wovhw7izzva0" />
              </div>

              {/* HISTORY OF PRESENTING COMPLAINT */}
              <div className="border-2 border-black" data-id="element-2226" data-mp="3rzpooak3ndiu">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2227" data-mp="3rzy5gdjcf2zb">
                  History of Presenting Complaint
                </h3>
                <textarea className="w-full p-3 min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.historyOfPresentingComplaint} onChange={e => setNotes({
                ...notes,
                historyOfPresentingComplaint: e.target.value
              })} data-id="element-2228" data-mp="3i21tjjil0405" />
              </div>

              {/* PAST MEDICAL HISTORY */}
              <div className="border-2 border-black" data-id="element-2229" data-mp="1yoejzq5q5hcl">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2230" data-mp="1ypzbt80nmm6r">
                  Past Medical History / General Co-Morbidities
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-2231" data-mp="1ozefxscd7vrj">
                  <tbody data-id="element-2232" data-mp="2co62a339xjwe">
                    {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA', 'RVD'].map(condition => {
                    const checked = notes.pastMedicalHistory?.includes(condition) || false;
                    return <tr key={condition} className="border-b border-gray-300" data-id="element-2233" data-mp="waz75njtu9on">
                          <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-2234" data-mp="js3gn9sdgali">
                            {condition}
                          </td>
                          <td className="border-r border-gray-300 p-2 text-center w-24" data-id="element-2235" data-mp="ymrupcbu8hcq">
                            <label className="flex items-center justify-center cursor-pointer" data-id="element-2236" data-mp="tqj85dvb9lvj">
                              <input type="checkbox" className="mr-2 h-4 w-4" checked={checked} onChange={e => {
                            const current = notes.pastMedicalHistory || [];
                            if (e.target.checked) {
                              setNotes({
                                ...notes,
                                pastMedicalHistory: [...current, condition]
                              });
                            } else {
                              setNotes({
                                ...notes,
                                pastMedicalHistory: current.filter(c => c !== condition)
                              });
                            }
                          }} data-id="element-2237" data-mp="60gjl7qluy1o" />
                              {checked ? 'Yes' : ''}
                            </label>
                          </td>
                          <td className="p-2" data-id="element-2238" data-mp="31878ljwk927">
                            <input type="text" placeholder="Notes (optional)" disabled={!checked} className="w-full p-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400" value={notes.pastMedicalHistoryNotes?.[condition] || ''} onChange={e => setNotes({
                          ...notes,
                          pastMedicalHistoryNotes: {
                            ...(notes.pastMedicalHistoryNotes || {}),
                            [condition]: e.target.value
                          }
                        })} data-id="element-2239" data-mp="19fzed6zmcq4o" />
                          </td>
                        </tr>;
                  })}
                  </tbody>
                </table>
              </div>

              {/* DRUG HISTORY */}
              <div className="border-2 border-black" data-id="element-2240" data-mp="7hvz2c5qw0am">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2241" data-mp="7j74shir3i4d">
                  Drug History
                </h3>
                <textarea className="w-full p-3 min-h-[60px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.drugHistory} onChange={e => setNotes({
                ...notes,
                drugHistory: e.target.value
              })} data-id="element-2242" data-mp="3tq59n2b01zgg" />
                <div className="p-3 border-t border-gray-300 bg-gray-50" data-id="element-2243" data-mp="3az5oydajfbsz">
                  <span className="font-semibold" data-id="element-2244" data-mp="3ax1flftvdklb">Any known allergy:</span>{' '}
                  <span className="text-red-600 font-medium" data-id="element-2245" data-mp="3fyqoopxj7o6r">
                    {patient.allergies}
                  </span>
                </div>
              </div>

              {/* FAMILY MEDICAL HISTORY */}
              <div className="border-2 border-black" data-id="element-2246" data-mp="3625l8915ul51">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2247" data-mp="2naz70mfeokuq">
                  Family Medical History
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-2248" data-mp="2wcffgomnucwb">
                  <tbody data-id="element-2249" data-mp="2wdqk0qnxjooh">
                    {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA'].map(condition => {
                    const checked = notes.familyMedicalHistory?.includes(condition) || false;
                    return <tr key={condition} className="border-b border-gray-300" data-id="element-2250" data-mp="3b24vfpzewlgn">
                          <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-2251" data-mp="3k2syxqr6j3fa">
                            {condition}
                          </td>
                          <td className="border-r border-gray-300 p-2 text-center w-24" data-id="element-2252" data-mp="2hmsgp9fwern9">
                            <label className="flex items-center justify-center cursor-pointer" data-id="element-2253" data-mp="2lo49fnu2jszo">
                              <input type="checkbox" className="mr-2 h-4 w-4" checked={checked} onChange={e => {
                            const current = notes.familyMedicalHistory || [];
                            if (e.target.checked) {
                              setNotes({
                                ...notes,
                                familyMedicalHistory: [...current, condition]
                              });
                            } else {
                              setNotes({
                                ...notes,
                                familyMedicalHistory: current.filter(c => c !== condition)
                              });
                            }
                          }} data-id="element-2254" data-mp="2qn7208sv46qr" />
                              {checked ? 'Yes' : ''}
                            </label>
                          </td>
                          <td className="p-2" data-id="element-2255" data-mp="1lhh8kc0mvqxw">
                            <input type="text" placeholder="Notes (e.g. relative, severity)" disabled={!checked} className="w-full p-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400" value={notes.familyMedicalHistoryNotes?.[condition] || ''} onChange={e => setNotes({
                          ...notes,
                          familyMedicalHistoryNotes: {
                            ...(notes.familyMedicalHistoryNotes || {}),
                            [condition]: e.target.value
                          }
                        })} data-id="element-2256" data-mp="1lg78jha808om" />
                          </td>
                        </tr>;
                  })}
                    {(() => {
                    const otherChecked = notes.familyMedicalHistory?.includes('OTHER') || false;
                    return <tr className="border-b border-gray-300" data-id="element-2257" data-mp="3ly5bw5ocg07u">
                          <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-2258" data-mp="3lwaomkplnw0l">
                            OTHER
                          </td>
                          <td className="border-r border-gray-300 p-2 text-center w-24" data-id="element-2259" data-mp="3vogffr4487e7">
                            <label className="flex items-center justify-center cursor-pointer" data-id="element-2260" data-mp="3qolqht3whrvs">
                              <input type="checkbox" className="mr-2 h-4 w-4" checked={otherChecked} onChange={e => {
                            const current = notes.familyMedicalHistory || [];
                            if (e.target.checked) {
                              setNotes({
                                ...notes,
                                familyMedicalHistory: [...current, 'OTHER']
                              });
                            } else {
                              setNotes({
                                ...notes,
                                familyMedicalHistory: current.filter(c => c !== 'OTHER')
                              });
                            }
                          }} data-id="element-2261" data-mp="32yzq52bsl6m7" />
                              {otherChecked ? 'Yes' : ''}
                            </label>
                          </td>
                          <td className="p-2" data-id="element-2262" data-mp="2h7wm23repy2h">
                            <input type="text" placeholder="Specify (e.g. condition, relative)" disabled={!otherChecked} className="w-full p-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400" value={notes.familyMedicalHistoryOther || ''} onChange={e => setNotes({
                          ...notes,
                          familyMedicalHistoryOther: e.target.value
                        })} data-id="element-2263" data-mp="3eo2cz0p4jc7c" />
                          </td>
                        </tr>;
                  })()}
                  </tbody>
                </table>
              </div>

              {/* OBSTETRICS AND GYNAECOLOGY HISTORY */}
              <div className="border-2 border-black" data-id="element-2264" data-mp="mspt824a1fun">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2265" data-mp="mq3kw09kawbd">
                  Obstetrics and Gynaecology History
                </h3>
                <textarea className="w-full p-3 min-h-[80px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.obgynHistory} onChange={e => setNotes({
                ...notes,
                obgynHistory: e.target.value
              })} data-id="element-2266" data-mp="2nuz36z07b67o" />
              </div>

              {/* SOCIAL HISTORY */}
              <div className="border-2 border-black" data-id="element-2267" data-mp="32j2acbpzcny2">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2268" data-mp="32iafx484cw9d">
                  Social History
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-2269" data-mp="3bk05s5mcrn51">
                  <tbody data-id="element-2270" data-mp="3bhnn1q8rt8qx">
                    <tr className="border-b border-gray-300" data-id="element-2271" data-mp="1m4k6rj257hsv">
                      <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-2272" data-mp="1m52bz56s96cx">
                        Alcohol
                      </td>
                      <td className="p-2" data-id="element-2273" data-mp="3sskjz9apq4c6">
                        <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.alcohol || ''} onChange={e => setNotes({
                        ...notes,
                        socialHistory: {
                          ...notes.socialHistory,
                          alcohol: e.target.value
                        }
                      })} data-id="element-2274" data-mp="3stm74c506ha9" />
                      </td>
                    </tr>
                    <tr className="border-b border-gray-300" data-id="element-2275" data-mp="355kv2t8m8iq3">
                      <td className="border-r border-gray-300 p-2 font-medium" data-id="element-2276" data-mp="zbuwwf9385e5">
                        Smoking
                      </td>
                      <td className="p-2" data-id="element-2277" data-mp="139smhj6rsti2">
                        <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.smoking || ''} onChange={e => setNotes({
                        ...notes,
                        socialHistory: {
                          ...notes.socialHistory,
                          smoking: e.target.value
                        }
                      })} data-id="element-2278" data-mp="elr021kgqmbb" />
                      </td>
                    </tr>
                    <tr className="border-b border-gray-300" data-id="element-2279" data-mp="3d4fu0fh1e0b1">
                      <td className="border-r border-gray-300 p-2 font-medium" data-id="element-2280" data-mp="384jmw7d1lsr9">
                        Occupation
                      </td>
                      <td className="p-2" data-id="element-2281" data-mp="342ycqtrw7skr">
                        <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.occupation || ''} onChange={e => setNotes({
                        ...notes,
                        socialHistory: {
                          ...notes.socialHistory,
                          occupation: e.target.value
                        }
                      })} data-id="element-2282" data-mp="1jknvj6pykclp" />
                      </td>
                    </tr>
                    <tr data-id="element-2283" data-mp="1elkmzcarro8z">
                      <td className="border-r border-gray-300 p-2 font-medium" data-id="element-2284" data-mp="1emvqrc47sw03">
                        Mental Status
                      </td>
                      <td className="p-2" data-id="element-2285" data-mp="m4qh40kgc1ri">
                        <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.mentalStatus || ''} onChange={e => setNotes({
                        ...notes,
                        socialHistory: {
                          ...notes.socialHistory,
                          mentalStatus: e.target.value
                        }
                      })} data-id="element-2286" data-mp="m3p43qjvl575" />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* REVIEW OF SYSTEMS */}
              <div className="border-2 border-black" data-id="element-2287" data-mp="6vus23jiv5n">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2288" data-mp="1ghhpzy691zia">
                  Review of Systems
                </h3>
                <div className="p-3 space-y-2" data-id="element-2289" data-mp="2thkwmfpx94e4">
                  {[{
                  key: 'rs',
                  label: 'R/S:'
                }, {
                  key: 'cvs',
                  label: 'CVS:'
                }, {
                  key: 'cns',
                  label: 'CNS:'
                }, {
                  key: 'git',
                  label: 'GIT:'
                }, {
                  key: 'gut',
                  label: 'GUT:'
                }, {
                  key: 'mss',
                  label: 'MSS:'
                }, {
                  key: 'is',
                  label: 'IS:'
                }].map(system => <div key={system.key} className="flex items-center" data-id="element-2290" data-mp="4pqsfg5sz84l">
                      <span className="font-semibold w-16" data-id="element-2291" data-mp="20sn5ezh56b1b">{system.label}</span>
                      <input type="text" className="flex-1 p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.reviewOfSystems?.[system.key as keyof typeof notes.reviewOfSystems] || ''} onChange={e => setNotes({
                    ...notes,
                    reviewOfSystems: {
                      ...notes.reviewOfSystems,
                      [system.key]: e.target.value
                    }
                  })} data-id="element-2292" data-mp="20uq56b83jij8" />
                    </div>)}
                </div>
              </div>

              {/* ON EXAMINATION */}
              <div className="border-2 border-black" data-id="element-2293" data-mp="3606jjfgtjbl6">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2294" data-mp="361rcx1recohg">
                  On Examination
                </h3>
                <div className="p-4 space-y-3" data-id="element-2295" data-mp="3f0uw05qkd3vy">
                  {[{
                  key: 'gc' as const,
                  label: 'G/C',
                  helper: 'Stable / Ill Looking / Unconscious'
                }, {
                  key: 'febrile' as const,
                  label: 'Temperature',
                  helper: 'Febrile / Afebrile / Lethargic'
                }, {
                  key: 'pjc' as const,
                  label: 'P | J | C',
                  helper: 'Pallor / Jaundice / Cyanosis'
                }, {
                  key: 's1s2' as const,
                  label: 'S1  S2',
                  helper: 'Heart sounds'
                }, {
                  key: 'rs' as const,
                  label: 'R/S',
                  helper: 'Respiratory system'
                }, {
                  key: 'pa' as const,
                  label: 'P/A',
                  helper: 'Per abdomen'
                }].map(field => <div key={field.key} className="grid grid-cols-[7rem_1fr] gap-3 items-start" data-id="element-2296" data-mp="2awfe8vc70ebj">
                      <div className="pt-2" data-id="element-2297" data-mp="2fwtmaddsno9k">
                        <p className="font-bold text-sm text-gray-900" data-id="element-2298" data-mp="2kttq0hn68exi">
                          {field.label}
                        </p>
                        <p className="text-xs text-gray-500 leading-tight" data-id="element-2299" data-mp="2pr3rivnu4h1p">
                          {field.helper}
                        </p>
                      </div>
                      <input type="text" value={notes.onExaminationDetails?.[field.key] || ''} onChange={e => setNotes({
                    ...notes,
                    onExaminationDetails: {
                      ...(notes.onExaminationDetails || {
                        gc: '',
                        febrile: '',
                        pjc: '',
                        s1s2: '',
                        rs: '',
                        pa: ''
                      }),
                      [field.key]: e.target.value
                    }
                  })} placeholder={`Enter ${field.label} findings`} className="w-full border-2 border-gray-300 rounded-md p-2 text-sm bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none" data-id="element-2300" data-mp="vfyfb2yomwar" />
                    </div>)}

                  {/* Additional notes (free text) */}
                  <div className="pt-3 border-t border-gray-200" data-id="element-2301" data-mp="28q4rxupar2ge">
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide" data-id="element-2302" data-mp="28n8tm59jebrf">
                      Additional Notes
                    </label>
                    <textarea className="w-full p-2 min-h-[80px] text-sm border-2 border-gray-300 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none" placeholder="Any other examination findings..." value={notes.onExamination} onChange={e => setNotes({
                    ...notes,
                    onExamination: e.target.value
                  })} data-id="element-2303" data-mp="1zn3ndyg2xjeu" />
                  </div>
                </div>
              </div>

              {/* SUMMARY OF SIGNIFICANT FINDINGS */}
              <div className="border-2 border-black" data-id="element-2304" data-mp="nf8y929nukj3">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2305" data-mp="ifmiom6d0s74">
                  Summary of Significant Findings
                </h3>
                <div className="p-3 space-y-2" data-id="element-2306" data-mp="xdyhozh7gd1g">
                  {[0, 1, 2, 3, 4, 5, 6, 7].map(i => <div key={i} className="flex items-start" data-id="element-2307" data-mp="sfn90s2nc8kt">
                      <span className="font-medium mr-2 mt-1" data-id="element-2308" data-mp="16c0oxwrj81x6">{i + 1}.</span>
                      <input type="text" className="flex-1 p-1 border-b border-gray-300 focus:border-blue-500 focus:outline-none" value={notes.significantFindings?.[i] || ''} onChange={e => {
                    const current = notes.significantFindings || Array(8).fill('');
                    const updated = [...current];
                    updated[i] = e.target.value;
                    setNotes({
                      ...notes,
                      significantFindings: updated
                    });
                  }} data-id="element-2309" data-mp="11es1phrj17p2" />
                    </div>)}
                </div>
              </div>

              {/* WORKING DIAGNOSIS */}
              <div className="border-2 border-black" data-id="element-2310" data-mp="3tbxl3bokko09">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2311" data-mp="2vn4c2b0o8w9w">
                  Working Diagnosis
                </h3>
                <div className="p-3 space-y-2" data-id="element-2312" data-mp="30n9yncs33sl4">
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => <div key={i} className="flex items-start" data-id="element-2313" data-mp="39lvolszvy9vx">
                      <span className="font-medium mr-2 mt-1" data-id="element-2314" data-mp="39nf0l7eidyrw">{i + 1}.</span>
                      <input type="text" className="flex-1 p-1 border-b border-gray-300 focus:border-blue-500 focus:outline-none" value={notes.workingDiagnosis?.[i] || ''} onChange={e => {
                    const current = notes.workingDiagnosis || Array(10).fill('');
                    const updated = [...current];
                    updated[i] = e.target.value;
                    setNotes({
                      ...notes,
                      workingDiagnosis: updated
                    });
                  }} data-id="element-2315" data-mp="kszwhodnjwn9" />
                    </div>)}
                </div>
              </div>

              {/* INVESTIGATIONS TO BE DONE */}
              <div className="border-2 border-black" data-id="element-2316" data-mp="1cnejcddhpwsp">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2317" data-mp="1copuiy99m6tu">
                  Investigations To Be Done
                </h3>
                <div className="p-4" data-id="element-2318" data-mp="370w63y62mpmo">
                  {/* LABORATORY TESTS */}
                  <h4 className="font-bold text-sm uppercase mb-2 border-b border-gray-400 pb-1" data-id="element-2319" data-mp="3q6atvkx40yec">
                    Laboratory Tests
                  </h4>
                  <table className="w-full border-collapse text-sm mb-4" data-id="element-2320" data-mp="3uy2ap3qgrpra">
                    <tbody data-id="element-2321" data-mp="3h6fcuzgvi96e">
                      {[['FBC', 'GRAVINDEX'], ['MPS/RDT', 'HVS'], ['CREATININE / U&E', 'URINALYSIS'], ['LIVER FUNCTION TEST', 'PAP SMEAR'], ['AAFB', 'CD4 COUNT'], ['URINE MICROSCOPY', 'URETHRAL SWAB'], ['VBG', 'VIRAL LOAD'], ['HEPATITIS PROFILE', 'RBS / FBS'], ['ABG', 'STOOL MICROSCOPY'], ['HORMONAL PROFILE', 'ANTI-MÜLLERIAN HORMONE'], ['RPR', 'HIV'], ['SPERM ANALYSIS', 'TESTOSTERONE'], ['DOA 6', '']].map((pair, idx) => <tr key={idx} className="border-b border-gray-200" data-id="element-2322" data-mp="2lv7q8gpcy51k" data-mpi={idx}>
                          {pair.map((test, ci) => {
                        if (!test) {
                          return <Fragment key={`empty-${ci}`} data-id="element-2323" data-mp="2uvnuhsdxdg7h">
                                  <td className="p-1.5 border-r border-gray-200 w-1/3" data-id="element-2324" data-mp="2pxcipc42kvmm"></td>
                                  <td className="p-1.5 w-1/6 border-r border-gray-300" data-id="element-2325" data-mp="2280aaegkgrld"></td>
                                </Fragment>;
                        }
                        const isOrdered = selectedEncounter.labRequests.some(lab => lab.tests.some(t => t.toLowerCase().includes(test.toLowerCase()) || test.toLowerCase().includes(t.toLowerCase())));
                        return <Fragment key={test} data-id="element-2326" data-mp="12wikzfl6935l" data-mpi={ci}>
                                <td className="p-1.5 font-medium border-r border-gray-200 w-1/3" data-id="element-2327" data-mp="12zwm6ki3n6bg">
                                  {test}
                                </td>
                                <td className="p-1.5 w-1/6 text-center border-r border-gray-300" data-id="element-2328" data-mp="1cvhnegfcg2k5">
                                  {isOrdered ? <span className="text-green-700 font-bold" data-id="element-2329" data-mp="1lnjli81xx6r9">
                                      ✓
                                    </span> : ''}
                                </td>
                              </Fragment>;
                      })}
                        </tr>)}
                    </tbody>
                  </table>

                  {/* RADIOLOGY SCANS */}
                  <h4 className="font-bold text-sm uppercase mb-2 border-b border-gray-400 pb-1" data-id="element-2330" data-mp="3g6cv5e9i9bju">
                    Radiology Scans
                  </h4>
                  <table className="w-full border-collapse text-sm mb-4" data-id="element-2331" data-mp="3675hsr2rtgwq">
                    <tbody data-id="element-2332" data-mp="7o6ucfly1dmk">
                      {[['PELVIC SCAN', 'ANOMALY SCAN'], ['OBS SCAN', 'CXR (CHEST X-RAY)'], ['HSG (HYSTEROSALPINGOGRAPHY)', '']].map((pair, idx) => <tr key={idx} className="border-b border-gray-200" data-id="element-2333" data-mp="3u1oobh80ityn" data-mpi={idx}>
                          {pair.map((scan, ci) => {
                        if (!scan) {
                          return <Fragment key={`empty-r-${ci}`} data-id="element-2334" data-mp="lohpvvy77ge2">
                                  <td className="p-1.5 border-r border-gray-200 w-1/3" data-id="element-2335" data-mp="lm4xspyq7pna"></td>
                                  <td className="p-1.5 w-1/6 border-r border-gray-300" data-id="element-2336" data-mp="1nddaw3hkw86l"></td>
                                </Fragment>;
                        }
                        const isOrdered = selectedEncounter.radiologyRequests.some(r => {
                          const a = r.scanType.toLowerCase();
                          const b = scan.toLowerCase();
                          return a.includes(b) || b.includes(a) || b.includes('cxr') && a.includes('cxr') || b.includes('hsg') && a.includes('hsg');
                        });
                        return <Fragment key={scan} data-id="element-2337" data-mp="2k3vzf93tjl0k" data-mpi={ci}>
                                <td className="p-1.5 font-medium border-r border-gray-200 w-1/3" data-id="element-2338" data-mp="2k572t7tcqqr5">
                                  {scan}
                                </td>
                                <td className="p-1.5 w-1/6 text-center border-r border-gray-300" data-id="element-2339" data-mp="38mxr24dyw3zl">
                                  {isOrdered ? <span className="text-green-700 font-bold" data-id="element-2340" data-mp="2yq2qzh21pvj7">
                                      ✓
                                    </span> : ''}
                                </td>
                              </Fragment>;
                      })}
                        </tr>)}
                    </tbody>
                  </table>

                  {/* ─── PENDING INVESTIGATIONS — RECALL ─── */}
                  {(() => {
                  const pendingLabs = selectedEncounter.labRequests.filter(l => l.status === 'Pending');
                  const pendingRads = selectedEncounter.radiologyRequests.filter(r => r.status === 'Pending');
                  const hasAny = pendingLabs.some(l => l.tests.length > 0 || l.otherTests.trim().length) || pendingRads.length > 0;
                  if (!hasAny) return null;
                  return <div className="mb-4 border border-amber-300 bg-amber-50 rounded-md p-3" data-id="element-2341" data-mp="qi7r7c7vdkbe">
                        <h4 className="font-bold text-xs uppercase text-amber-800 mb-2" data-id="element-2342" data-mp="mg2zwk4w8zrx">
                          Pending Investigations (Recall to make changes)
                        </h4>
                        <ul className="space-y-1.5 text-sm" data-id="element-2343" data-mp="377morgfbyt9x">
                          {pendingLabs.flatMap(lab => lab.tests.map(testName => <li key={`${lab.id}-${testName}`} className="flex items-center justify-between bg-white border border-amber-200 rounded px-2 py-1" data-id="element-2344" data-mp="1hpy77btox2d9">
                                <span data-id="element-2345" data-mp="1qp34ketimpnu">
                                  <span className="font-medium" data-id="element-2346" data-mp="1vhco9d41etho">
                                    {testName}
                                  </span>{' '}
                                  <span className="text-xs text-gray-500" data-id="element-2347" data-mp="20fxmx83n5540">
                                    · Laboratory ·{' '}
                                    {new Date(lab.requestedAt).toLocaleTimeString()}
                                  </span>
                                </span>
                                <Button size="sm" variant="outline" className="text-red-600 border-red-400 hover:bg-red-50 text-xs" onClick={() => handleRecallLabTest(lab.id, testName)} data-id="element-2348" data-mp="g1ds707p2nxs">
                                  <RotateCcw className="w-3 h-3 mr-1" data-id="element-2349" data-mp="3uv2yfty3qj57" /> Recall
                                </Button>
                              </li>))}
                          {pendingLabs.filter(l => l.otherTests.trim().length > 0).map(lab => <li key={`${lab.id}-other`} className="flex items-center justify-between bg-white border border-amber-200 rounded px-2 py-1" data-id="element-2350" data-mp="3bxs8x129v42y">
                                <span data-id="element-2351" data-mp="2je364ybr84b6">
                                  <span className="font-medium" data-id="element-2352" data-mp="2ei4khiht90eg">
                                    {lab.otherTests}
                                  </span>{' '}
                                  <span className="text-xs text-gray-500" data-id="element-2353" data-mp="yq0pjomx0suh">
                                    · Other lab test
                                  </span>
                                </span>
                                <Button size="sm" variant="outline" className="text-red-600 border-red-400 hover:bg-red-50 text-xs" onClick={() => handleRecallLabTest(lab.id, lab.otherTests, true)} data-id="element-2354" data-mp="p07hgxlzevuz">
                                  <RotateCcw className="w-3 h-3 mr-1" data-id="element-2355" data-mp="3tefm9ufilz3f" /> Recall
                                </Button>
                              </li>)}
                          {pendingRads.map(rad => <li key={rad.id} className="flex items-center justify-between bg-white border border-amber-200 rounded px-2 py-1" data-id="element-2356" data-mp="1u05mu3wrb2li">
                              <span data-id="element-2357" data-mp="1l6k9mp08pte7">
                                <span className="font-medium" data-id="element-2358" data-mp="1g7g180l53zox">
                                  {rad.scanType}
                                </span>{' '}
                                <span className="text-xs text-gray-500" data-id="element-2359" data-mp="1b7bhm1oxqouh">
                                  · Radiology ·{' '}
                                  {new Date(rad.requestedAt).toLocaleTimeString()}
                                </span>
                              </span>
                              <Button size="sm" variant="outline" className="text-red-600 border-red-400 hover:bg-red-50 text-xs" onClick={() => handleRecallRadiology(rad.id)} data-id="element-2360" data-mp="2wlfu4rlkip2o">
                                <RotateCcw className="w-3 h-3 mr-1" data-id="element-2361" data-mp="s4jf1hdpmu3i" /> Recall
                              </Button>
                            </li>)}
                        </ul>
                        <p className="text-xs text-amber-700 mt-2 italic" data-id="element-2362" data-mp="3vmkti9don32n">
                          Recall removes the request before the lab or radiology
                          department processes it. Once a test is completed, it
                          becomes part of the permanent record.
                        </p>
                      </div>;
                })()}

                  <div className="flex gap-3 mb-4" data-id="element-2363" data-mp="1junzth5dtj59">
                    <Button variant="outline" onClick={() => setShowLabModal(true)} className="flex-1" data-id="element-2364" data-mp="1otr1qsq2f990">
                      <FlaskConical className="w-4 h-4 mr-2" data-id="element-2365" data-mp="vivpjqv56jlz" /> Request
                      Laboratory Test
                    </Button>
                    <Button variant="outline" onClick={() => setShowRadModal(true)} className="flex-1 border-purple-600 text-purple-600 hover:bg-purple-50" data-id="element-2366" data-mp="19atppc6vl560">
                      <Scan className="w-4 h-4 mr-2" data-id="element-2367" data-mp="1e93n7tg2ohus" /> Request Radiology Scan
                    </Button>
                  </div>

                  <div className="border-t border-gray-300 pt-2" data-id="element-2368" data-mp="1s961z6t32v5t">
                    <span className="font-semibold" data-id="element-2369" data-mp="1s6js30iq03kf">Other investigations:</span>{' '}
                    {selectedEncounter.labRequests.map(l => l.otherTests).filter(Boolean).join(', ') || ''}
                  </div>

                  {/* Investigation Results — View File per test */}
                  {(selectedEncounter.labRequests.some(l => l.status === 'Completed') || selectedEncounter.radiologyRequests.some(r => r.status === 'Completed')) && <div className="mt-4 pt-4 border-t-2 border-black" data-id="element-2370" data-mp="3b37h3vh5t21y">
                      <h4 className="font-bold mb-3 uppercase text-center" data-id="element-2371" data-mp="2w6zyv0pbaapb">
                        Investigation Results
                      </h4>
                      <table className="w-full border-collapse text-sm" data-id="element-2372" data-mp="317e8uoah3upz">
                        <thead data-id="element-2373" data-mp="2n6tqft8h00x0">
                          <tr className="bg-gray-100" data-id="element-2374" data-mp="2n7mqycgu5u6p">
                            <th className="border border-black p-1.5 text-left" data-id="element-2375" data-mp="2s4muogq7qkun">
                              Test / Scan
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-2376" data-mp="lcm13cnho87j">
                              Type
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-2377" data-mp="ei0dzkvnv2bk">
                              Performed By
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-2378" data-mp="jj7npphd6bkv">
                              Completed
                            </th>
                            <th className="border border-black p-1.5 text-center w-32" data-id="element-2379" data-mp="9qig55oxphut">
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody data-id="element-2380" data-mp="sghgwsob1ap">
                          {selectedEncounter.labRequests.filter(l => l.status === 'Completed').flatMap(lab => lab.tests.filter(t => !(lab.removedTestNames || []).includes(t)).map(testName => <tr key={`${lab.id}-${testName}`} data-id="element-2381" data-mp="35paxb2todil9">
                                    <td className="border border-black p-1.5 font-medium" data-id="element-2382" data-mp="31nz3sm7okd7s">
                                      {testName}
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-2383" data-mp="3anccvdrw6zrz">
                                      Laboratory
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-2384" data-mp="3pel9w7y4jnf5">
                                      —
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-2385" data-mp="26rc2k9ezczy">
                                      {lab.completedAt ? new Date(lab.completedAt).toLocaleString() : ''}
                                    </td>
                                    <td className="border border-black p-1.5 text-center" data-id="element-2386" data-mp="2yhkstsxz69n">
                                      <div className="flex justify-center gap-2" data-id="element-2387" data-mp="7xc6c3ian8xg">
                                        <Button size="sm" variant="outline" onClick={() => setViewingTest({
                              kind: 'lab',
                              labId: lab.id,
                              testName
                            })} data-id="element-2388" data-mp="cv45nil9pxbo">
                                          <Eye className="w-3.5 h-3.5 mr-1" data-id="element-2389" data-mp="14n1v4s3zswis" />{' '}
                                          View File
                                        </Button>
                                        <button onClick={() => setRemoveTestModal({
                              isOpen: true,
                              labId: lab.id,
                              testName,
                              reason: ''
                            })} className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded p-1 transition-colors print:hidden" title="Remove duplicate test" data-id="element-2390" data-mp="2u3nowxnlx13r">
                                          <Trash2 className="w-4 h-4" data-id="element-2391" data-mp="2z28p4x2uzks7" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>))}
                          {selectedEncounter.labRequests.filter(l => l.status === 'Completed' && l.otherTests && !(l.removedTestNames || []).includes(l.otherTests)).map(lab => <tr key={`${lab.id}-other`} data-id="element-2392" data-mp="2wd0zuhxoghe">
                                <td className="border border-black p-1.5 font-medium" data-id="element-2393" data-mp="2xxt7dgrznc3">
                                  {lab.otherTests}{' '}
                                  <span className="text-xs text-gray-500" data-id="element-2394" data-mp="3q85lp8w977yb">
                                    (Other)
                                  </span>
                                </td>
                                <td className="border border-black p-1.5" data-id="element-2395" data-mp="mkc9eyp0zbjn">
                                  Laboratory
                                </td>
                                <td className="border border-black p-1.5" data-id="element-2396" data-mp="vwjjmdb66y05">—</td>
                                <td className="border border-black p-1.5 text-xs" data-id="element-2397" data-mp="rx2ddhnx5wtx">
                                  {lab.completedAt ? new Date(lab.completedAt).toLocaleString() : ''}
                                </td>
                                <td className="border border-black p-1.5 text-center" data-id="element-2398" data-mp="d0blrukhlpev">
                                  <div className="flex justify-center gap-2" data-id="element-2399" data-mp="90143knc1ak6">
                                    <Button size="sm" variant="outline" onClick={() => setViewingTest({
                              kind: 'lab',
                              labId: lab.id,
                              testName: lab.otherTests
                            })} data-id="element-2400" data-mp="mz96emeskysl">
                                      <Eye className="w-3.5 h-3.5 mr-1" data-id="element-2401" data-mp="2afo2i1x3ajal" /> View
                                      File
                                    </Button>
                                    <button onClick={() => setRemoveTestModal({
                              isOpen: true,
                              labId: lab.id,
                              testName: lab.otherTests,
                              reason: ''
                            })} className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded p-1 transition-colors print:hidden" title="Remove duplicate test" data-id="element-2402" data-mp="2fe8u5cy9zunf">
                                      <Trash2 className="w-4 h-4" data-id="element-2403" data-mp="wdqcltc3cqcd" />
                                    </button>
                                  </div>
                                </td>
                              </tr>)}
                          {selectedEncounter.radiologyRequests.filter(r => r.status === 'Completed').map(rad => <tr key={rad.id} data-id="element-2404" data-mp="8pyolsh6uov9">
                                <td className="border border-black p-1.5 font-medium" data-id="element-2405" data-mp="8p71z75kk1h4">
                                  {rad.scanType}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-2406" data-mp="3uyria2ntxigp">
                                  Radiology
                                </td>
                                <td className="border border-black p-1.5" data-id="element-2407" data-mp="3lx0ckxtahbmu">
                                  {rad.radiographerName || '—'}
                                </td>
                                <td className="border border-black p-1.5 text-xs" data-id="element-2408" data-mp="1dm9411p0jb5h">
                                  {rad.completedAt ? new Date(rad.completedAt).toLocaleString() : ''}
                                </td>
                                <td className="border border-black p-1.5 text-center" data-id="element-2409" data-mp="18ned1cgw6gaa">
                                  <Button size="sm" variant="outline" onClick={() => setViewingTest({
                            kind: 'rad',
                            radId: rad.id
                          })} data-id="element-2410" data-mp="uw9ksvfusq9z">
                                    <Eye className="w-3.5 h-3.5 mr-1" data-id="element-2411" data-mp="g0tn8gg2ec8u" /> View
                                    File
                                  </Button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>}
                </div>
              </div>

              {/* DRUG TREATMENT / PRESCRIPTION */}
              <div className="border-2 border-black" data-id="element-2412" data-mp="1g7l5jpaouh7">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2413" data-mp="g9uculmaosbe">
                  Drug Treatment / Prescription
                  {selectedEncounter.prescriptions.length > 0 && <span className="ml-2 text-xs font-normal text-gray-600 lowercase" data-id="element-2414" data-mp="p2nu2olay1rw">
                      ({selectedEncounter.prescriptions.length} of 20 drugs)
                    </span>}
                </h3>
                <div className="max-h-[420px] overflow-y-auto" data-id="element-2415" data-mp="u2k16wpaq9bo">
                  <table className="w-full border-collapse text-sm text-center" data-id="element-2416" data-mp="u4d88d3tmvk7">
                    <thead data-id="element-2417" data-mp="mibv5nasr6q9">
                      <tr className="bg-gray-100" data-id="element-2418" data-mp="mhiun42fldgk">
                        <th className="border border-black p-1.5 w-10" data-id="element-2419" data-mp="ihryeku4y54n">No.</th>
                        <th className="border border-black p-1.5 text-left" data-id="element-2420" data-mp="diorsvyibquk">
                          Drug Name
                        </th>
                        <th className="border border-black p-1.5" data-id="element-2421" data-mp="8j2gis28okoe">Dose</th>
                        <th className="border border-black p-1.5" data-id="element-2422" data-mp="3l0uby9io58p">Route</th>
                        <th className="border border-black p-1.5" data-id="element-2423" data-mp="3uzospuylyxa5">Frequency</th>
                        <th className="border border-black p-1.5" data-id="element-2424" data-mp="3uxls6gztxlr6">
                          No. of Days
                        </th>
                        <th className="border border-black p-1.5" data-id="element-2425" data-mp="3lza1keyyqnyq">Signature</th>
                        <th className="border border-black p-1.5 w-16" data-id="element-2426" data-mp="3gznny4f835pe">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-2427" data-mp="3c2x210gqlfw2">
                      {selectedEncounter.prescriptions.length > 0 ? selectedEncounter.prescriptions.map((rx, i) => <tr key={rx.id} className={rx.status === 'Dispensed' ? 'bg-green-50' : rx.status === 'Prescribed' || rx.status === 'Not Dispensed' ? 'bg-amber-50' : ''} data-id="element-2428" data-mp="36tvygulm9mmk" data-mpi={i}>
                              <td className="border border-black p-1.5" data-id="element-2429" data-mp="2t293cy3euk5d">
                                {i + 1}
                              </td>
                              <td className="border border-black p-1.5 text-left font-medium" data-id="element-2430" data-mp="20gg2wv5l7hlr">
                                {rx.drugName}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-2431" data-mp="1xds7nn5dlj99">
                                {rx.dose}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-2432" data-mp="1sefhotj7jw2g">
                                {rx.route}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-2433" data-mp="2251qa3sibmbn">
                                {rx.frequency}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-2434" data-mp="1efftto3irl7c">
                                {rx.days}
                              </td>
                              <td className="border border-black p-1.5 text-xs" data-id="element-2435" data-mp="1odwq90l88s0s">
                                {rx.status === 'Dispensed' && <span className="text-green-700 font-bold" data-id="element-2436" data-mp="1jcza0tjs281x">
                                    ✓ Dispensed
                                  </span>}
                                {(rx.status === 'Prescribed' || rx.status === 'Not Dispensed') && <span className="font-bold text-amber-700" data-id="element-2437" data-mp="37brwvglehtcb">
                                    ℞ Prescribed — not in stock
                                  </span>}
                                {rx.status === 'Pending' && <span className="italic text-gray-500" data-id="element-2438" data-mp="2xfov4bugnwpw">
                                    Pending
                                  </span>}
                                {rx.pharmacistName && <div className="text-gray-500 italic mt-0.5" data-id="element-2439" data-mp="3ca37htjis7vl">
                                    {rx.pharmacistName}
                                  </div>}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-2440" data-mp="7utmyey0ztz3">
                                {rx.status === 'Pending' ? <button onClick={() => handleDeletePrescription(rx.id)} className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded p-1 transition-colors" title="Remove this drug" aria-label={`Remove ${rx.drugName}`} data-id="element-2441" data-mp="2l58oknyfsqng">
                                    <Trash2 className="w-4 h-4" data-id="element-2442" data-mp="2yvlwiqbbw5cs" />
                                  </button> : <span className="text-gray-300" data-id="element-2443" data-mp="2u3kndd9giuqz">—</span>}
                              </td>
                            </tr>) : Array.from({
                      length: 20
                    }, (_, idx) => idx + 1).map(i => <tr key={i} data-id="element-2444" data-mp="19bf2xrgeeg7o">
                              <td className="border border-black p-3" data-id="element-2445" data-mp="19cggq3ostgt3">{i}</td>
                              <td className="border border-black p-3" data-id="element-2446" data-mp="1dcigu8evy269"></td>
                              <td className="border border-black p-3" data-id="element-2447" data-mp="1ic3fehq588ig"></td>
                              <td className="border border-black p-3" data-id="element-2448" data-mp="1ideh8c01366x"></td>
                              <td className="border border-black p-3" data-id="element-2449" data-mp="1naerkz3wuv3u"></td>
                              <td className="border border-black p-3" data-id="element-2450" data-mp="1s9hkxmaj3cvz"></td>
                              <td className="border border-black p-3" data-id="element-2451" data-mp="1x0zjy4szr3eb"></td>
                              <td className="border border-black p-3" data-id="element-2452" data-mp="1x33zjk095quf"></td>
                            </tr>)}
                    </tbody>
                  </table>
                </div>
                <div className="p-3 border-t border-gray-300 flex gap-3" data-id="element-2453" data-mp="25zl3o3a78ogo">
                  <Button variant="outline" onClick={() => setShowRxModal(true)} className="flex-1 text-green-600 border-green-600 hover:bg-green-50" disabled={selectedEncounter.prescriptions.length >= 20} data-id="element-2454" data-mp="2b1acdc9y8q1l">
                    <Pill className="w-4 h-4 mr-2" data-id="element-2455" data-mp="kpnbr1450hsw" />
                    {selectedEncounter.prescriptions.length >= 20 ? 'Maximum 20 Drugs Reached' : 'Prescribe Medication'}
                  </Button>
                  {selectedEncounter.prescriptions.some(rx => rx.status === 'Pending') && <Button onClick={handleSendToPharmacy} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white" data-id="element-2456" data-mp="13lvmuljtzmw4">
                      <Pill className="w-4 h-4 mr-2" data-id="element-2457" data-mp="35fmhd3xpmesc" /> Send Prescription to
                      Pharmacy
                    </Button>}
                </div>
              </div>

              {/* FOLLOW UP / REVIEW DATE */}
              <div className="border-2 border-black" data-id="element-2458" data-mp="1fziucoydw6tj">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2459" data-mp="1kwsjs4lrwihd">
                  Next Follow Up / Review Date (If Any)
                </h3>
                <table className="w-full border-collapse text-sm text-center" data-id="element-2460" data-mp="1b67ksg26tbxx">
                  <thead data-id="element-2461" data-mp="x6y4zhhw8juh">
                    <tr className="bg-gray-100" data-id="element-2462" data-mp="x6ev8o2ed1rj">
                      <th className="border border-black p-1 w-10" data-id="element-2463" data-mp="x914qt8ulrce">No.</th>
                      <th className="border border-black p-1" data-id="element-2464" data-mp="126suphpxn3dy">Date</th>
                      <th className="border border-black p-1 w-10" data-id="element-2465" data-mp="n9ac6x3ulmey">No.</th>
                      <th className="border border-black p-1" data-id="element-2466" data-mp="s7vc0vf6u42v">Date</th>
                      <th className="border border-black p-1 w-10" data-id="element-2467" data-mp="s83ncivnx1by">No.</th>
                      <th className="border border-black p-1" data-id="element-2468" data-mp="2ho34am5ivv25">Date</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-2469" data-mp="2ml39kuujsts7">
                    {[0, 1, 2, 3, 4].map(row => <tr key={row} data-id="element-2470" data-mp="1ab7w7tiddeqn">
                        <td className="border border-black p-1 font-medium" data-id="element-2471" data-mp="1aec4p32vawn4">
                          {row + 1}
                        </td>
                        <td className="border border-black p-1" data-id="element-2472" data-mp="10ly1s701zjzd">
                          {row === 0 ? <input type="date" className="w-full p-1 text-center focus:ring-2 focus:ring-blue-500 focus:outline-none" value={selectedEncounter.reviewDate ? new Date(selectedEncounter.reviewDate).toISOString().split('T')[0] : ''} onChange={e => {
                        if (e.target.value) {
                          updateEncounter(selectedEncounter.id, {
                            reviewDate: new Date(e.target.value).toISOString()
                          });
                        }
                      }} data-id="element-2473" data-mp="vmlj7yg7sz2l" /> : ''}
                        </td>
                        <td className="border border-black p-1 font-medium" data-id="element-2474" data-mp="1vpjl91liyh5p">
                          {row + 6}
                        </td>
                        <td className="border border-black p-2" data-id="element-2475" data-mp="25ld412pu76vx"></td>
                        <td className="border border-black p-1 font-medium" data-id="element-2476" data-mp="2akpppk4z27wx">
                          {row + 11}
                        </td>
                        <td className="border border-black p-2" data-id="element-2477" data-mp="3gzqd31fxlhrw"></td>
                      </tr>)}
                  </tbody>
                </table>
                <div className="border-t border-black p-3" data-id="element-2478" data-mp="184io3h9tcne6">
                  <h4 className="font-bold uppercase text-sm mb-2" data-id="element-2479" data-mp="182xtxsrev6gu">Notes</h4>
                  <textarea className="w-full p-2 border border-gray-300 rounded text-sm min-h-[80px] focus:ring-2 focus:ring-blue-500 focus:outline-none" placeholder="Add any notes related to the follow-up or review (e.g. reason for review, instructions to patient, expected investigations)…" value={reviewDateNotes} onChange={e => setReviewDateNotes(e.target.value)} data-id="element-2480" data-mp="134wav7tzj75d" />
                </div>
              </div>

              {/* NAME OF MEDICAL PRACTITIONER */}
              <div className="border-2 border-black p-4 mt-6" data-id="element-2481" data-mp="u596cd5yll83">
                <p className="font-bold text-sm" data-id="element-2482" data-mp="u3ptkwjihsb2">
                  Name of the Medical Practitioner:{' '}
                  <span className="font-normal border-b border-black inline-block min-w-[250px] ml-2" data-id="element-2483" data-mp="29nug77fsd2in">
                    {selectedEncounter.clinicalNote?.clinicianName || currentUser?.fullName || ''}
                  </span>
                </p>
              </div>
            </div>
          </fieldset>

          {/* VERSION HISTORY VIEWER */}
          {selectedEncounter.clinicalNote?.editHistory && selectedEncounter.clinicalNote.editHistory.length > 0 && <div className="mt-8 border-t-2 border-dashed border-gray-300 pt-6 print:hidden" data-id="element-2484" data-mp="2vughd74p036j">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center" data-id="element-2485" data-mp="30t9tav4bzm4l">
                  <History className="w-5 h-5 mr-2 text-gray-500" data-id="element-2486" data-mp="30v49jw4nqq2c" />
                  Version History
                </h3>
                <div className="space-y-4" data-id="element-2487" data-mp="2r2yfmguuhyki">
                  {selectedEncounter.clinicalNote.editHistory.map((edit, idx) => <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-2488" data-mp="3erq34uxhpsqy" data-mpi={idx}>
                        <div className="flex justify-between items-start mb-2" data-id="element-2489" data-mp="3teorgh11w71g">
                          <div data-id="element-2490" data-mp="3og50u3bii72r">
                            <p className="font-semibold text-gray-900" data-id="element-2491" data-mp="3oh6gkl3h3hqt">
                              Edited by {edit.editedByName}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-2492" data-mp="3fgi3ptptfnfi">
                              {new Date(edit.editedAt).toLocaleString()}
                            </p>
                          </div>
                          <Badge variant="warning" data-id="element-2493" data-mp="35px4q568cgw2">Version {idx + 1}</Badge>
                        </div>
                        <div className="bg-white border border-gray-100 p-3 rounded text-sm text-gray-700 mt-2" data-id="element-2494" data-mp="30qaum4lp7grh">
                          <span className="font-semibold text-gray-900" data-id="element-2495" data-mp="2wqs6kzykypkf">
                            Reason for change:
                          </span>{' '}
                          {edit.reason}
                        </div>
                        <details className="mt-3" data-id="element-2496" data-mp="2mte3e21o3xa2">
                          <summary className="text-sm text-blue-600 cursor-pointer hover:underline font-medium" data-id="element-2497" data-mp="12a1x36lvtuaa">
                            View previous note content
                          </summary>
                          <div className="mt-2 p-3 bg-white border border-gray-200 rounded text-xs text-gray-600 max-h-60 overflow-y-auto font-mono whitespace-pre-wrap" data-id="element-2498" data-mp="1c76ih47wllq1">
                            {JSON.stringify(edit.previousNote, null, 2)}
                          </div>
                        </details>
                      </div>)}
                </div>
              </div>}
        </div>

        {/* ═══ MODALS ═══ */}
        {removeTestModal.isOpen && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-2499" data-mp="xb7cq089nxo2">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" data-id="element-2500" data-mp="rhv4bqqmkop">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center text-red-600" data-id="element-2501" data-mp="4stew0mxkboz">
                <Trash2 className="w-5 h-5 mr-2" data-id="element-2502" data-mp="3hvp5abmg1eys" /> Remove Duplicate Test
              </h3>
              <p className="text-sm text-gray-600 mb-4" data-id="element-2503" data-mp="3cwc7wwxy4phy">
                Why are you removing "{removeTestModal.testName}"? This action
                will be logged in the audit trail.
              </p>
              <div className="mb-4" data-id="element-2504" data-mp="3mtgz5b6nfb5m">
                <textarea className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none" rows={3} value={removeTestModal.reason} onChange={e => setRemoveTestModal({
              ...removeTestModal,
              reason: e.target.value
            })} placeholder="e.g., Duplicate entry, entered by mistake..." data-id="element-2505" data-mp="3msfhgnv4nqex" />
              </div>
              <div className="flex justify-end space-x-3" data-id="element-2506" data-mp="2b6z2ww17o071">
                <Button variant="outline" onClick={() => setRemoveTestModal({
              isOpen: false,
              labId: '',
              testName: '',
              reason: ''
            })} data-id="element-2507" data-mp="2b8jvwh7vnb2s">
                  Cancel
                </Button>
                <Button variant="danger" onClick={handleRemoveTestConfirm} disabled={!removeTestModal.reason.trim()} data-id="element-2508" data-mp="lvd8mq3vf77m">
                  Confirm Removal
                </Button>
              </div>
            </div>
          </div>}

        {/* Lab Modal */}
        {showLabModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50" data-id="element-2509" data-mp="2zn7t67ocgvb">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col" data-id="element-2510" data-mp="1setl4hdvh17l">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-2511" data-mp="1xc387qdsh0s9">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-2512" data-mp="1vx0s5kx607qg">
                  Request Laboratory Tests
                </h3>
                <button onClick={() => setShowLabModal(false)} data-id="element-2513" data-mp="1qypj3cep219a">
                  <X className="w-5 h-5 text-gray-400" data-id="element-2514" data-mp="1lxs2h49c1f9w" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto flex-1" data-id="element-2515" data-mp="1gzqafttxi5ma">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-id="element-2516" data-mp="2emgtubkl668g">
                  <div data-id="element-2517" data-mp="2ens2opsw246f">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2518" data-mp="29mb1et6hsfpb">
                      HAEMATOLOGY
                    </h4>
                    {['FBC', 'Blood Group', 'CD4 Count', 'MPS/RDT', 'AAFB', 'Viral Load'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2519" data-mp="2sl6rbsbbi7xc">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2520" data-mp="30wfjvmpboc8r" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-2521" data-mp="1hbuhy9b42z3">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2522" data-mp="6eakq5punps0">
                      CHEMISTRY
                    </h4>
                    {['Urea', 'Creatinine', 'U&E', 'FBS', 'RBS', 'Liver Function Test', 'VBG', 'ABG'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2523" data-mp="15oiasc2nnzzk">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2524" data-mp="d037gmsvz642" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-2525" data-mp="25olw9ys2ohej">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2526" data-mp="25pvssjja1hj2">
                      SEROLOGY
                    </h4>
                    {['HIV', 'Syphilis', 'RPR', 'HBs Ag', 'HCV', 'Hepatitis Profile', 'Gravindex (Pregnancy Test)'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2527" data-mp="2ollus6r7mdwy">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2528" data-mp="2flyxnx5iju9p" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-2529" data-mp="tza9u65t3ojd">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2530" data-mp="u027dmiyrwca">
                      ENDOCRINOLOGY
                    </h4>
                    {['Hormonal Profile', 'Testosterone', 'Anti-Müllerian Hormone (AMH)', 'Beta HCG (Quantitative)'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2531" data-mp="b6yalsag93dg">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2532" data-mp="2tgu4f2a9moj4" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-2533" data-mp="1wr3dfd16nji2">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2534" data-mp="1wrwatne94wnj">
                      URINE / FERTILITY
                    </h4>
                    {['Urinalysis', 'Urine Microscopy', 'Urine Culture', 'Sperm Analysis', 'Sperm Culture', 'PAP Smear'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2535" data-mp="1i5frpnfc06x3">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2536" data-mp="1856r7xeb3zbw" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-2537" data-mp="85cyg61bk36b">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2538" data-mp="84k11vo92q0u">
                      MICROBIOLOGY / SWABS
                    </h4>
                    {['HVS', 'Urethral Swab', 'Stool Microscopy'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2539" data-mp="cx4totvf6x2s">
                          <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2540" data-mp="3ler5jruwz6cj" />{' '}
                          {test}
                        </label>)}
                  </div>
                  <div data-id="element-2541" data-mp="3ka7tvgd0ucv8">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2542" data-mp="3k9pmbnkwsc80">
                      OTHER
                    </h4>
                    {['DOA 6 (Drugs of Abuse)'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2543" data-mp="3u7vrqf5xc0hm">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2544" data-mp="7tcdcigy8lb5" />{' '}
                        {test}
                      </label>)}
                  </div>
                </div>
                <div className="mt-6" data-id="element-2545" data-mp="hxsstaqio0ef">
                  <Input label="Other Tests (Free Text)" value={otherTests} onChange={e => setOtherTests(e.target.value)} placeholder="Enter any other tests required..." data-id="element-2546" data-mp="hyu157g5e8sg" />
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50" data-id="element-2547" data-mp="14vdxo6w13i87">
                <Button variant="outline" onClick={() => setShowLabModal(false)} data-id="element-2548" data-mp="10u2bycg9zh5a">
                  Cancel
                </Button>
                <Button onClick={handleRequestLab} data-id="element-2549" data-mp="1skyf2lcja7fd">Send to Laboratory</Button>
              </div>
            </div>
          </div>}

        {/* Rad Modal */}
        {showRadModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50" data-id="element-2550" data-mp="3rt828xc9m34g">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-id="element-2551" data-mp="3d30ubbjvw2i0">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-2552" data-mp="3i2lufpasigwb">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-2553" data-mp="342ubrb9bwcbz">
                  Request Radiology Scan
                </h3>
                <button onClick={() => setShowRadModal(false)} data-id="element-2554" data-mp="3915lllzmimu7">
                  <X className="w-5 h-5 text-gray-400" data-id="element-2555" data-mp="2u7slgc3bh47t" />
                </button>
              </div>
              <div className="p-6" data-id="element-2556" data-mp="2z5uf1qydclxj">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2557" data-mp="seuz8z41p9vu">
                  Scan Type <span className="text-red-500" data-id="element-2558" data-mp="sd1r1fdsahlq">*</span>
                </label>
                <select required value={scanType} onChange={e => setScanType(e.target.value)} className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm" data-id="element-2559" data-mp="xd67j5eozcby">
                  <option value="" data-id="element-2560" data-mp="2mvcv2ycsqvtq">— Select a scan —</option>
                  <option value="Pelvic Scan" data-id="element-2561" data-mp="2mtrxt0z3kys6">Pelvic Scan</option>
                  <option value="Anomaly Scan" data-id="element-2562" data-mp="30lgelhkakbfh">Anomaly Scan</option>
                  <option value="OBS Scan" data-id="element-2563" data-mp="2wl65hw17d4we">OBS Scan</option>
                  <option value="Abdominal Scan" data-id="element-2564" data-mp="2wkmqoo6umwml">Abdominal Scan</option>
                  <option value="Abdomen/Pelvis Scan" data-id="element-2565" data-mp="3ajm9hf7okhau">
                    Abdomen and Pelvic Scan (Abdomen/Pelvis Scan)
                  </option>
                  <option value="CXR (Chest X-Ray)" data-id="element-2566" data-mp="35jrizcrthtqb">CXR (Chest X-Ray)</option>
                  <option value="HSG (Hysterosalpingography)" data-id="element-2567" data-mp="1g6wib58p5m75">
                    HSG (Hysterosalpingography)
                  </option>
                </select>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50" data-id="element-2568" data-mp="pdm98hf35t6e">
                <Button variant="outline" onClick={() => setShowRadModal(false)} data-id="element-2569" data-mp="ubw9h6fo3jyv">
                  Cancel
                </Button>
                <Button onClick={handleRequestRad} className="bg-purple-600 hover:bg-purple-700" data-id="element-2570" data-mp="141yzqyvemjti">
                  Send to Radiology
                </Button>
              </div>
            </div>
          </div>}

        {/* Rx Modal */}
        {showRxModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 z-50 overflow-y-auto" data-id="element-2571" data-mp="gcehki72rqla">
            <div className="min-h-full flex items-start sm:items-center justify-center p-4" data-id="element-2572" data-mp="le3s7wqdy28u">
              <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[calc(100vh-2rem)] flex flex-col my-4" data-id="element-2573" data-mp="1rscskrltcmp5">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0 sticky top-0 bg-white rounded-t-lg z-10" data-id="element-2574" data-mp="1wtsfkp7jx3a6">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-2575" data-mp="3cbyj0tzyq2mp">
                    Prescribe Medication
                  </h3>
                  <button onClick={() => setShowRxModal(false)} data-id="element-2576" data-mp="37i3zbqkcxnnm">
                    <X className="w-5 h-5 text-gray-400" data-id="element-2577" data-mp="32kaub0usizox" />
                  </button>
                </div>
                <div className="p-6 overflow-y-auto flex-1" data-id="element-2578" data-mp="2xmsi91lh84cp">
                  <div className="grid grid-cols-2 gap-4" data-id="element-2579" data-mp="2tjw71vy9nais">
                    <Input label="Drug Name" className="col-span-2" value={rxForm.drugName} onChange={e => setRxForm({
                  ...rxForm,
                  drugName: e.target.value
                })} required data-id="element-2580" data-mp="2tmie7uh8vo0h" />
                    <Input label="Dose" placeholder="e.g. 500mg" value={rxForm.dose} onChange={e => setRxForm({
                  ...rxForm,
                  dose: e.target.value
                })} required data-id="element-2581" data-mp="19oxwvcw8xog5" />
                    <Select label="Route" options={[{
                  value: 'PO',
                  label: 'Oral (PO)'
                }, {
                  value: 'IV',
                  label: 'Intravenous (IV)'
                }, {
                  value: 'IM',
                  label: 'Intramuscular (IM)'
                }, {
                  value: 'Topical',
                  label: 'Topical'
                }, {
                  value: 'Vaginal',
                  label: 'Vaginal'
                }, {
                  value: 'Rectal',
                  label: 'Rectal'
                }]} value={rxForm.route} onChange={e => setRxForm({
                  ...rxForm,
                  route: e.target.value
                })} data-id="element-2582" data-mp="qyzi6dxy0x3j" />
                    <Input label="Frequency" placeholder="e.g. BD, TDS" value={rxForm.frequency} onChange={e => setRxForm({
                  ...rxForm,
                  frequency: e.target.value
                })} required data-id="element-2583" data-mp="24wq6qzhk8vmq" />
                    <Input label="No. of Days" type="number" value={rxForm.days} onChange={e => setRxForm({
                  ...rxForm,
                  days: e.target.value
                })} required data-id="element-2584" data-mp="2qp2kxubmx3rw" />
                  </div>
                  <div className="mt-4 flex justify-end" data-id="element-2585" data-mp="27zmb2ge27yyt">
                    <Button onClick={handleAddRx} variant="outline" className="text-green-600 border-green-600 hover:bg-green-50" data-id="element-2586" data-mp="2mnh27scf5v9s">
                      + Add to Prescription List
                    </Button>
                  </div>

                  {selectedEncounter.prescriptions.length > 0 && <div className="mt-6 border-t border-gray-200 pt-4" data-id="element-2587" data-mp="1p68wvmykg75w">
                      <div className="flex justify-between items-center mb-2" data-id="element-2588" data-mp="4og5adosg75s">
                        <h4 className="font-medium text-sm" data-id="element-2589" data-mp="9hssqkhqe7wy">
                          Current List ({selectedEncounter.prescriptions.length}{' '}
                          of 20)
                        </h4>
                        {selectedEncounter.prescriptions.length >= 20 && <span className="text-xs text-amber-600 font-medium" data-id="element-2590" data-mp="ng92kkaw4ejb">
                            Maximum reached
                          </span>}
                      </div>
                      <ul className="space-y-2 max-h-64 overflow-y-auto pr-1 border border-gray-200 rounded p-2 bg-gray-50" data-id="element-2591" data-mp="eesskdnzmefm">
                        {selectedEncounter.prescriptions.map((rx, idx) => <li key={rx.id} className="text-sm bg-white p-2 rounded flex justify-between items-center border border-gray-100 gap-2" data-id="element-2592" data-mp="2z5a09tqwljuw" data-mpi={idx}>
                            <span className="flex-1 min-w-0 truncate" data-id="element-2593" data-mp="3jgw0oskhx3ew">
                              <span className="text-gray-400 mr-2" data-id="element-2594" data-mp="3enb2p0iwk9fp">
                                {idx + 1}.
                              </span>
                              {rx.drugName} - {rx.dose}
                            </span>
                            <span className="text-gray-500 text-xs whitespace-nowrap" data-id="element-2595" data-mp="34o2b2ebif6wi">
                              {rx.frequency} x {rx.days} days
                            </span>
                            {rx.status === 'Pending' ? <button onClick={() => handleDeletePrescription(rx.id)} className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded p-1 transition-colors flex-shrink-0" title="Remove this drug" aria-label={`Remove ${rx.drugName}`} data-id="element-2596" data-mp="3septhtj1xexb">
                                <Trash2 className="w-4 h-4" data-id="element-2597" data-mp="shzp2z43p264" />
                              </button> : <span className="text-xs text-gray-400 flex-shrink-0 italic" data-id="element-2598" data-mp="wg749pix1vf7">
                                {rx.status}
                              </span>}
                          </li>)}
                      </ul>
                    </div>}
                </div>
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50 flex-shrink-0 sticky bottom-0 rounded-b-lg z-10" data-id="element-2599" data-mp="1g5hjl2er2ecs">
                  <Button onClick={() => setShowRxModal(false)} className="bg-blue-600 hover:bg-blue-700 text-white" data-id="element-2600" data-mp="1k61k8fyj803o">
                    Done
                  </Button>
                </div>
              </div>
            </div>
          </div>}

        {/* Image Zoom Modal */}
        {zoomImage && <div className="fixed inset-0 bg-black bg-opacity-90 flex flex-col items-center justify-center z-50" onClick={() => setZoomImage(null)} data-id="element-2601" data-mp="y4dhbl729jh7">
            <div className="absolute top-4 right-4 flex items-center gap-3 z-10" data-id="element-2602" data-mp="175bdjhb9cmp8">
              <span className="text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded" data-id="element-2603" data-mp="1255s4ivkzwfl">
                Scroll to zoom • Click outside to close
              </span>
              <button className="text-white hover:text-gray-300 bg-black bg-opacity-50 rounded-full p-2" onClick={() => setZoomImage(null)} data-id="element-2604" data-mp="1c3mm7optgr5v">
                <X className="w-6 h-6" data-id="element-2605" data-mp="1pwue7hht7oo3" />
              </button>
            </div>
            <div className="max-w-[90vw] max-h-[85vh] overflow-auto rounded-lg" onClick={e => e.stopPropagation()} data-id="element-2606" data-mp="1lu7szzfn91zv">
              <img src={zoomImage} alt="Radiology Scan — Zoom up to 200%" className="min-w-[60vw] max-w-none" style={{
            width: '180%',
            cursor: 'grab'
          }} data-id="element-2607" data-mp="1uuvzm92pk02q" />
            </div>
            <p className="text-gray-400 text-xs mt-3" data-id="element-2608" data-mp="24goshpl84d00">
              Image displayed at up to 200% zoom. Scroll within the frame to
              pan.
            </p>
          </div>}

        {/* ─── TEST FILE MODAL (View File per investigation) ─── */}
        {viewingTest && selectedEncounter && (() => {
        const patient = getPatient(selectedEncounter.patientId);
        if (!patient) return null;
        const lab = viewingTest.kind === 'lab' ? selectedEncounter.labRequests.find(l => l.id === viewingTest.labId) : null;
        const rad = viewingTest.kind === 'rad' ? selectedEncounter.radiologyRequests.find(r => r.id === viewingTest.radId) : null;
        if (!lab && !rad) return null;
        const focusedTest = viewingTest.kind === 'lab' ? viewingTest.testName : null;
        // Filter lab.results by the requested test name when possible
        const labResultsFiltered = lab && lab.results ? focusedTest ? Object.fromEntries(Object.entries(lab.results).filter(([k]) => k.toLowerCase().includes(focusedTest.toLowerCase()) || focusedTest.toLowerCase().includes(k.toLowerCase()) ||
        // FBC sub-tests, urinalysis (UA_), testosterone — keep all if focused test is one of these umbrella tests
        ['fbc', 'urinalysis', 'testosterone', 'sperm analysis'].some(u => focusedTest.toLowerCase().includes(u)))) : lab.results : null;
        return <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 print:p-0 print:bg-white" data-id="element-2609" data-mp="3b81c34fv51ti">
                <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto print:max-h-none print:max-w-none print:shadow-none print:rounded-none print:overflow-visible test-modal-print" data-id="element-2610" data-mp="2iiufiazyymaw">
                  <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center z-10 print:hidden" data-id="element-2611" data-mp="2sggvl0t08jwg">
                    <h3 className="text-lg font-semibold text-gray-900" data-id="element-2612" data-mp="2x9jw5sw78tm5">
                      {lab ? `Laboratory Result — ${focusedTest || lab.tests.filter(t => !(lab.removedTestNames || []).includes(t)).join(', ')}` : `Radiology Result — ${rad?.scanType}`}
                    </h3>
                    <div className="flex items-center space-x-2" data-id="element-2613" data-mp="1jg3i8tiwp12f">
                      <Button variant="outline" size="sm" onClick={handlePrintTestModal} data-id="element-2614" data-mp="1je91zsikxx4o">
                        <Printer className="w-4 h-4 mr-1" data-id="element-2615" data-mp="19gckmiz1u5y4" /> Print
                      </Button>
                      <Button variant="primary" size="sm" onClick={handleDownloadTestPdf} disabled={isGeneratingTestPdf} data-id="element-2616" data-mp="vpg4hrmb7h7y">
                        <Download className="w-4 h-4 mr-1" data-id="element-2617" data-mp="qrel15kz1fvy" />
                        {isGeneratingTestPdf ? 'Generating...' : 'Download PDF'}
                      </Button>
                      <button onClick={closeTestModal} className="p-1.5 hover:bg-gray-100 rounded" aria-label="Close" data-id="element-2618" data-mp="ctzqxnjrwjxn">
                        <X className="w-5 h-5 text-gray-500" data-id="element-2619" data-mp="3pbhcw2n9sk2h" />
                      </button>
                    </div>
                  </div>

                  <div ref={testModalRef} className="p-8 bg-white" data-id="element-2620" data-mp="76xjhgwd207n">
                    <div className="flex justify-between items-start mb-6 border-b-2 border-black pb-4" data-id="element-2621" data-mp="34j2718cb8ih">
                      <div className="flex items-center" data-id="element-2622" data-mp="h3iqg7rxxle4">
                        <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED" className="h-16 w-16 object-contain mr-3" data-id="element-2623" data-mp="h1zayje3zede" />
                        <div data-id="element-2624" data-mp="lw3h50fjvidf">
                          <h1 className="text-xl font-bold text-pink-600 uppercase" data-id="element-2625" data-mp="lwvd4cd27i48">
                            BMED MEDICAL CENTRE
                          </h1>
                          <p className="text-xs italic text-gray-600" data-id="element-2626" data-mp="uwrx40oi7ssy">
                            Caring Healing Leading
                          </p>
                        </div>
                      </div>
                      <div className="text-right text-sm" data-id="element-2627" data-mp="3tn1hutk5d9w5">
                        <p className="font-bold uppercase border-b border-black pb-1 mb-2" data-id="element-2628" data-mp="1i7dktuodupb">
                          {lab ? 'Laboratory Report' : 'Radiology Report'}
                        </p>
                        <p data-id="element-2629" data-mp="b8siez0xzvgo">
                          <span className="font-semibold" data-id="element-2630" data-mp="b7h80gczrpgl">Patient:</span>{' '}
                          {patient.firstName} {patient.lastName}
                        </p>
                        <p data-id="element-2631" data-mp="l6zqesd9736j">
                          <span className="font-semibold" data-id="element-2632" data-mp="l7rgxorzuapy">File No:</span>{' '}
                          {patient.bmcFileNumber}
                        </p>
                        <p data-id="element-2633" data-mp="2my30ngj8efqk">
                          <span className="font-semibold" data-id="element-2634" data-mp="2myuxeuokejif">Age/Sex:</span>{' '}
                          {patient.age} / {patient.sex}
                        </p>
                        <p data-id="element-2635" data-mp="2r1hq0xt288go">
                          <span className="font-semibold" data-id="element-2636" data-mp="2jgp3vny3wgul">Date:</span>{' '}
                          {new Date((lab?.completedAt || rad?.completedAt) ?? Date.now()).toLocaleString()}
                        </p>
                        {lab?.performedBy && <p data-id="element-2637" data-mp="25qllx969f9ay">
                            <span className="font-semibold" data-id="element-2638" data-mp="32f19x0tq1aly">Performed By:</span>{' '}
                            {lab.performedBy}
                          </p>}
                      </div>
                    </div>

                    {lab && <div data-id="element-2639" data-mp="2tcicwxcm9s7m">
                        <h2 className="text-lg font-bold mb-3 uppercase" data-id="element-2640" data-mp="2of8o9jx1xkku">
                          {focusedTest || 'Tests Performed'}
                        </h2>

                        {/* ─── Structured FBC Form ─── */}
                        {focusedTest && isFBCTest(focusedTest) ? <div className="space-y-4" data-id="element-2641" data-mp="30wrlhus8o1f5">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-2642" data-mp="30vzqal2k05pe">
                              <h3 className="font-bold uppercase" data-id="element-2643" data-mp="35o9mujy6c40n">
                                Full Blood Count (FBC) Report
                              </h3>
                            </div>
                            {fbcStructure.map(cat => <div key={cat.category} className="border border-black" data-id="element-2644" data-mp="3jn97zhmha0s2">
                                <div className="bg-gray-100 p-2 font-bold border-b border-black text-xs uppercase" data-id="element-2645" data-mp="3ojzqscpo3ah6">
                                  {cat.category}
                                </div>
                                <table className="w-full border-collapse text-sm" data-id="element-2646" data-mp="3fnidp3xqt2is">
                                  <thead data-id="element-2647" data-mp="1z9ig3zmddoyp">
                                    <tr className="bg-gray-50" data-id="element-2648" data-mp="2471tyyf5nzq4">
                                      <th className="border border-black p-1.5 text-left" data-id="element-2649" data-mp="2445uv6rkn503">
                                        Test
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-2650" data-mp="1rnmys5n298qw">
                                        Result
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-2651" data-mp="20pw5slj4ejzj">
                                        Reference Range
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-24" data-id="element-2652" data-mp="1818gmev93agp">
                                        Unit
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody data-id="element-2653" data-mp="u7zcasm5tf51">
                                    {cat.tests.map(t => <tr key={t.name} data-id="element-2654" data-mp="p9xusc4dtnvo">
                                        <td className="border border-black p-1.5" data-id="element-2655" data-mp="p7blo81uf0bc">
                                          {t.name}
                                        </td>
                                        <td className="border border-black p-1.5 font-bold" data-id="element-2656" data-mp="2u97csg7q938d">
                                          {lab.results && lab.results[t.name] || '—'}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-2657" data-mp="26envcv0y65ro">
                                          {t.range}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-2658" data-mp="2fby70hhofwx4">
                                          {t.unit}
                                        </td>
                                      </tr>)}
                                  </tbody>
                                </table>
                              </div>)}
                          </div> : focusedTest && isUrinalysisTest(focusedTest) ? <div data-id="element-2659" data-mp="4ta7jg7a3w2t">
                            <div className="text-center border-b-2 border-black pb-2 mb-6" data-id="element-2660" data-mp="3w5d06csybo18">
                              <h3 className="font-bold uppercase" data-id="element-2661" data-mp="3s4japezv2168">
                                Urinalysis Report
                              </h3>
                            </div>
                            <div className="max-w-md mx-auto space-y-2" data-id="element-2662" data-mp="3i63krfckkxyb">
                              {urinalysisTests.map(t => {
                      const key = `UA_${t.name}`;
                      const value = lab.results && (lab.results[key] || lab.results[t.name]) || '';
                      return <div key={t.name} className="flex items-center text-sm" data-id="element-2663" data-mp="17c7u5iyy6u38">
                                    <span className="w-16 font-bold" data-id="element-2664" data-mp="1uq8bvo7knhib">
                                      {t.name}
                                    </span>
                                    <span className="mx-3 text-gray-400" data-id="element-2665" data-mp="1luslxi9xtmi0">
                                      -
                                    </span>
                                    <span className="flex-1 border-b border-gray-400 pb-0.5 font-medium" data-id="element-2666" data-mp="1bxo0xlrtvx2s">
                                      {value || '\u00A0'}
                                    </span>
                                  </div>;
                    })}
                            </div>
                            <div className="max-w-md mx-auto mt-10 grid grid-cols-2 gap-4 text-sm" data-id="element-2667" data-mp="2sdh21wiipeqn">
                              <div data-id="element-2668" data-mp="2xaqqp9y31mdf">
                                <p className="font-semibold mb-2" data-id="element-2669" data-mp="2x8wdkht1yyjw">
                                  Dr.'s Name:
                                </p>
                                <p className="border-b border-black pb-0.5 min-h-[1.25rem]" data-id="element-2670" data-mp="32h5q7q5iti9g">
                                  {lab.results && lab.results['UA_DR_NAME'] || '\u00A0'}
                                </p>
                              </div>
                              <div data-id="element-2671" data-mp="3q4m5ns7otiiz">
                                <p className="font-semibold mb-2" data-id="element-2672" data-mp="3q3uc0mxnhuvc">Signature:</p>
                                <p className="border-b border-black pb-0.5 min-h-[1.25rem]" data-id="element-2673" data-mp="3l5stc344zxke">
                                  {lab.results && lab.results['UA_DR_SIG'] || '\u00A0'}
                                </p>
                              </div>
                            </div>
                          </div> : focusedTest && isTestosteroneTest(focusedTest) ? <div className="space-y-2" data-id="element-2674" data-mp="20t3id67q72go">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-2675" data-mp="g0ut2nkp31is">
                              <h3 className="font-bold uppercase" data-id="element-2676" data-mp="b1qjhvtuz1rx">
                                Testosterone Report
                              </h3>
                            </div>
                            <table className="w-full border-collapse text-sm" data-id="element-2677" data-mp="kymt64vhtty2">
                              <thead data-id="element-2678" data-mp="yye1pq38qi4o">
                                <tr className="bg-gray-100" data-id="element-2679" data-mp="yxcpvjeehrlw">
                                  <th className="border border-black p-1.5 text-left" data-id="element-2680" data-mp="tzud1hx9is8m">
                                    Test
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-32" data-id="element-2681" data-mp="18qjrqu9xmph8">
                                    Result
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-2682" data-mp="1hpx17my23e1y">
                                    Reference (Male)
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-2683" data-mp="1wbkmxcjwqqmx">
                                    Reference (Female)
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-20" data-id="element-2684" data-mp="1nbp9f12vqjjq">
                                    Unit
                                  </th>
                                </tr>
                              </thead>
                              <tbody data-id="element-2685" data-mp="1djryzwjp3vm2">
                                {testosteroneTests.map(t => <tr key={t.name} data-id="element-2686" data-mp="1iddyso4rg50g">
                                    <td className="border border-black p-1.5" data-id="element-2687" data-mp="13notqhtjrguw">
                                      {t.name}
                                    </td>
                                    <td className="border border-black p-1.5 font-bold" data-id="element-2688" data-mp="18jx47hwsulvq">
                                      {lab.results && (lab.results[`TESTO_${t.name}`] || lab.results[t.name]) || '—'}
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-2689" data-mp="2wihiuqd8vq19">
                                      {t.rangeMale}
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-2690" data-mp="2nkdxbu29et9g">
                                      {t.rangeFemale}
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-2691" data-mp="3faa94d0xix5a">
                                      {t.unit}
                                    </td>
                                  </tr>)}
                              </tbody>
                            </table>
                          </div> : focusedTest && isHormonalProfileTest(focusedTest) ? <div data-id="element-2692" data-mp="1ux1jc8a5ztrr">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-2693" data-mp="ad64gmqmceqm">
                              <h3 className="font-bold uppercase" data-id="element-2694" data-mp="fbhcqt19mh6q">
                                Hormonal Profile Report
                              </h3>
                            </div>
                            <table className="w-full border-collapse text-sm" data-id="element-2695" data-mp="o3ssnkyr6m8g">
                              <thead data-id="element-2696" data-mp="o5vt6yxj7xrf">
                                <tr className="bg-gray-100" data-id="element-2697" data-mp="t3xapffb7p0s">
                                  <th className="border border-black p-1.5 text-left" data-id="element-2698" data-mp="t5i4h2tsv3xl">
                                    Test Description
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-32" data-id="element-2699" data-mp="1323a9qdn8j2h">
                                    Value
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-2700" data-mp="34tq3lyl0cq24">
                                    Reference Range
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-20" data-id="element-2701" data-mp="2djk8w3od8fiw">
                                    Unit
                                  </th>
                                </tr>
                              </thead>
                              <tbody data-id="element-2702" data-mp="2nhhyauhyoid3">
                                {hormonalProfileTests.map(h => <Fragment key={h.key} data-id="element-2703" data-mp="2ih3nx5sw0wbw">
                                    <tr className="align-top" data-id="element-2704" data-mp="1uung648m9v2s">
                                      <td className="border border-black p-1.5 font-medium" data-id="element-2705" data-mp="1puj0ggfj94dm">
                                        {h.name}
                                      </td>
                                      <td className="border border-black p-1.5 font-bold" data-id="element-2706" data-mp="24l8ctm4qcpj2">
                                        {lab.results && (lab.results[`HP_${h.key}`] || lab.results[h.name] || lab.results[h.key]) || '—'}
                                      </td>
                                      <td className="border border-black p-1.5 text-xs" data-id="element-2707" data-mp="3nid42vvizb31">
                                        {h.ranges.map(r => <div key={r.label} data-id="element-2708" data-mp="39ovi74fd9m9u">
                                            <span className="font-medium" data-id="element-2709" data-mp="3dt35qya38w6h">
                                              {r.label}:
                                            </span>{' '}
                                            {r.value}
                                          </div>)}
                                      </td>
                                      <td className="border border-black p-1.5 text-xs" data-id="element-2710" data-mp="1a3plun5grrx">
                                        {h.unit}
                                      </td>
                                    </tr>
                                    <tr data-id="element-2711" data-mp="2m1vomb2brchf">
                                      <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-2712" data-mp="2lyrgx3pnhym0">
                                        Method: {h.method}
                                      </td>
                                    </tr>
                                  </Fragment>)}
                              </tbody>
                            </table>
                          </div> : focusedTest && isAMHTest(focusedTest) ? <div data-id="element-2713" data-mp="3v2nkmihxfc4j">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-2714" data-mp="3v1vyrze4ssrg">
                              <h3 className="font-bold uppercase" data-id="element-2715" data-mp="3r1tz1vryi9et">
                                Anti Mullerian Hormone (AMH) Report
                              </h3>
                            </div>
                            <table className="w-full border-collapse text-sm" data-id="element-2716" data-mp="1p9fgqvli32xv">
                              <thead data-id="element-2717" data-mp="375hl3rce526s">
                                <tr className="bg-gray-100" data-id="element-2718" data-mp="374g65bs97vjs">
                                  <th className="border border-black p-1.5 text-left" data-id="element-2719" data-mp="3c3suk0yrxaoh">
                                    Test Description
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-32" data-id="element-2720" data-mp="3kwmck65luo61">
                                    Value
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-2721" data-mp="2t682ym4mjy06">
                                    Reference Range (by age, ng/mL)
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-20" data-id="element-2722" data-mp="32767d5iz67ja">
                                    Unit
                                  </th>
                                </tr>
                              </thead>
                              <tbody data-id="element-2723" data-mp="hfnnfhnb49kp">
                                <tr className="align-top" data-id="element-2724" data-mp="his2ja2b8pq5">
                                  <td className="border border-black p-1.5 font-medium" data-id="element-2725" data-mp="3pakm95johkqs">
                                    {amhStructure.name}
                                  </td>
                                  <td className="border border-black p-1.5 font-bold" data-id="element-2726" data-mp="1xqmzcp4vone">
                                    {lab.results && (lab.results[amhStructure.storageKey] || lab.results[focusedTest] || lab.results['AMH'] || lab.results['Anti-Müllerian Hormone'] || lab.results['Anti Mullerian Hormone (AMH)']) || '—'}
                                  </td>
                                  <td className="border border-black p-1.5 text-xs" data-id="element-2727" data-mp="2iluefafofdrn">
                                    {amhStructure.ageRanges.map(a => <div key={a.ageBand} data-id="element-2728" data-mp="28jiczsxq2e2d">
                                        <span className="font-medium" data-id="element-2729" data-mp="2chpkrya7k51f">
                                          {a.ageBand}:
                                        </span>{' '}
                                        {a.range}
                                      </div>)}
                                  </td>
                                  <td className="border border-black p-1.5 text-xs" data-id="element-2730" data-mp="1ovrdy6njo66k">
                                    {amhStructure.unit}
                                  </td>
                                </tr>
                                <tr data-id="element-2731" data-mp="1krjno51fui68">
                                  <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-2732" data-mp="1ksmholu250qd">
                                    Method: {amhStructure.method}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                            <div className="mt-3 border border-amber-300 bg-amber-50 rounded p-3 text-xs" data-id="element-2733" data-mp="uqafgvfrg8yl">
                              <p className="font-bold uppercase mb-1" data-id="element-2734" data-mp="zgqs4dbrbqjw">
                                Interpretation
                              </p>
                              <ul className="list-disc pl-5 space-y-0.5 text-gray-700" data-id="element-2735" data-mp="13i2hqiumsbs3">
                                {amhStructure.interpretation.map((line, i) => <li key={i} data-id="element-2736" data-mp="1dgi8gkpqxj12" data-mpi={i}>{line}</li>)}
                              </ul>
                            </div>
                          </div> : focusedTest && isHCGTest(focusedTest) ? <div data-id="element-2737" data-mp="3fahil2r4tuc6">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-2738" data-mp="3k6g5elwtb4ah">
                              <h3 className="font-bold uppercase" data-id="element-2739" data-mp="21cn3jq8ru4q2">
                                Beta HCG (Quantitative) Report
                              </h3>
                            </div>
                            <table className="w-full border-collapse text-sm" data-id="element-2740" data-mp="1sja19xothqrb">
                              <thead data-id="element-2741" data-mp="1nkp1u0he3b3x">
                                <tr className="bg-gray-100" data-id="element-2742" data-mp="1nlrofw7oire1">
                                  <th className="border border-black p-1.5 text-left" data-id="element-2743" data-mp="2kapkdhr60vcs">
                                    Test Description
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-32" data-id="element-2744" data-mp="2g7jh8izcnkaj">
                                    Value
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-2745" data-mp="26aoibyz5zhvq">
                                    Reference Range (mIU/mL)
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-20" data-id="element-2746" data-mp="uv9ov565z85d">
                                    Unit
                                  </th>
                                </tr>
                              </thead>
                              <tbody data-id="element-2747" data-mp="119xa42sjx5sc">
                                {(() => {
                        const hcgValue = lab.results && (lab.results[hcgStructure.storageKey] || lab.results[focusedTest] || lab.results['HCG'] || lab.results['Beta HCG']) || '';
                        const abnormal = isHCGAbnormal(hcgValue);
                        return <>
                                      <tr className="align-top" data-id="element-2748" data-mp="3qtr07b9jzlrx">
                                        <td className="border border-black p-1.5 font-medium" data-id="element-2749" data-mp="3vslfi38bas7a">
                                          {hcgStructure.name}
                                        </td>
                                        <td className={`border border-black p-1.5 font-bold ${abnormal ? 'text-red-600 bg-red-50' : ''}`} data-id="element-2750" data-mp="3788mtfw3rfku">
                                          {hcgValue || '—'}
                                          {abnormal && <div className="text-xs font-normal mt-0.5" data-id="element-2751" data-mp="3v3l5jmj2oaca">
                                              Above normal threshold (&gt; 6
                                              mIU/mL)
                                            </div>}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-2752" data-mp="hpq5klnekxj8">
                                          {hcgStructure.ranges.map(r => <div key={r.label} data-id="element-2753" data-mp="277mygdpiploz">
                                              <span className="font-medium" data-id="element-2754" data-mp="277d7onwnfai8">
                                                {r.label}:
                                              </span>{' '}
                                              {r.range}
                                            </div>)}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-2755" data-mp="1oa2ijw4qdxgi">
                                          {hcgStructure.unit}
                                        </td>
                                      </tr>
                                      <tr data-id="element-2756" data-mp="228k5hrmtb5xd">
                                        <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-2757" data-mp="229lfdss3dmdi">
                                          Method: {hcgStructure.method}
                                        </td>
                                      </tr>
                                    </>;
                      })()}
                              </tbody>
                            </table>
                          </div> : labResultsFiltered && Object.keys(labResultsFiltered).length > 0 ? <table className="w-full border-collapse text-sm" data-id="element-2758" data-mp="j7hxu40tq6x7">
                            <thead data-id="element-2759" data-mp="e9gf5k7b89m9">
                              <tr className="bg-gray-100" data-id="element-2760" data-mp="a8wdc3bskhts">
                                <th className="border border-black p-2 text-left" data-id="element-2761" data-mp="a9fydswqnmc1">
                                  Parameter
                                </th>
                                <th className="border border-black p-2 text-left" data-id="element-2762" data-mp="24m5saan3wvjf">
                                  Result
                                </th>
                              </tr>
                            </thead>
                            <tbody data-id="element-2763" data-mp="2irfuhsydrje0">
                              {Object.entries(labResultsFiltered).map(([k, v]) => <tr key={k} data-id="element-2764" data-mp="29su5beyel649">
                                    <td className="border border-black p-2 font-medium" data-id="element-2765" data-mp="29py6loeqeder">
                                      {k}
                                    </td>
                                    <td className="border border-black p-2" data-id="element-2766" data-mp="32i1ct3fu561n">
                                      {v}
                                    </td>
                                  </tr>)}
                            </tbody>
                          </table> : <p className="text-gray-500 italic" data-id="element-2767" data-mp="1h3n58bjaw8gw">
                            No structured results recorded for this test.
                          </p>}

                        {lab.labComments && <div className="mt-6 border border-gray-300 bg-gray-50 rounded p-4" data-id="element-2768" data-mp="1ehmfknblclv7">
                            <h3 className="font-bold uppercase mb-2 text-sm" data-id="element-2769" data-mp="19j2vksgk5k5h">
                              Comments / Observations / Interpretation
                            </h3>
                            <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800" data-id="element-2770" data-mp="1ikasb63fecxg">
                              {lab.labComments}
                            </pre>
                          </div>}
                      </div>}

                    {rad && <div data-id="element-2771" data-mp="14ljfro47ozbf">
                        <h2 className="text-lg font-bold mb-3 uppercase" data-id="element-2772" data-mp="14mulhth7wh56">
                          {rad.scanType}
                        </h2>
                        {rad.radiographerName && <p className="text-sm mb-3" data-id="element-2773" data-mp="c2dvp4b64ryq">
                            <span className="font-semibold" data-id="element-2774" data-mp="74cd0khnmuns">
                              Sonographer / Radiographer:
                            </span>{' '}
                            {rad.radiographerName}
                          </p>}
                        {rad.report && <div className="mb-4 border border-black p-4" data-id="element-2775" data-mp="3rol8qx0wxa7e">
                            <pre className="whitespace-pre-wrap font-sans text-sm" data-id="element-2776" data-mp="juu2382cm2u">
                              {rad.report}
                            </pre>
                          </div>}
                        {rad.images && rad.images.length > 0 && <div data-id="element-2777" data-mp="eaq9xzgzc3fx">
                            <div className="flex justify-between items-center mb-3 print:hidden" data-id="element-2778" data-mp="ebrhvv2p89tf">
                              <h3 className="font-bold" data-id="element-2779" data-mp="j8rnk4vmzak0">Scan Images</h3>
                              <div className="flex items-center space-x-2 bg-gray-100 px-3 py-1.5 rounded-lg" data-id="element-2780" data-mp="o95zhy0cz4nb">
                                <button onClick={() => setTestImageZoom(z => Math.max(10, z - 10))} className="p-1 hover:bg-gray-200 rounded" aria-label="Zoom out" data-id="element-2781" data-mp="t7h51wjmeszq">
                                  <ZoomOut className="w-4 h-4" data-id="element-2782" data-mp="1yuvbaymd37i0" />
                                </button>
                                <input type="range" min={10} max={200} step={10} value={testImageZoom} onChange={e => setTestImageZoom(Number(e.target.value))} className="w-32" data-id="element-2783" data-mp="2wilttsgumysz" />
                                <button onClick={() => setTestImageZoom(z => Math.min(200, z + 10))} className="p-1 hover:bg-gray-200 rounded" aria-label="Zoom in" data-id="element-2784" data-mp="14vqjqzcgf3ja">
                                  <ZoomIn className="w-4 h-4" data-id="element-2785" data-mp="1nlq81k0h0eln" />
                                </button>
                                <span className="text-sm font-medium w-12 text-right" data-id="element-2786" data-mp="1jl6aifbzj8yz">
                                  {testImageZoom}%
                                </span>
                              </div>
                            </div>
                            <div className="space-y-4 print:space-y-2" data-id="element-2787" data-mp="279yzfru6wgk3">
                              {rad.images.map((img, idx) => <div key={idx} className="border-2 border-gray-300 rounded overflow-auto bg-gray-50 max-h-[600px] print:max-h-none print:overflow-visible" data-id="element-2788" data-mp="22ih7fta59qb9" data-mpi={idx}>
                                  <div style={{
                        transform: `scale(${testImageZoom / 100})`,
                        transformOrigin: 'top left',
                        display: 'inline-block'
                      }} data-id="element-2789" data-mp="2jsbdv5ioelh4">
                                    <img src={img} alt={`${rad.scanType} ${idx + 1}`} className="block" data-id="element-2790" data-mp="2xq9gxerjqvhb" />
                                  </div>
                                </div>)}
                            </div>
                          </div>}
                      </div>}
                  </div>
                </div>
              </div>;
      })()}
      </div>;
  }
  return <div className="space-y-6" data-id="element-2791" data-mp="1q6dw72bvt6lu">
      <div className="flex justify-between items-center" data-id="element-2792" data-mp="1q7fi61ufrdic">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2793" data-mp="1lg78d4p71q4n">
          Clinician Dashboard
        </h1>
        <div className="flex space-x-2" data-id="element-2794" data-mp="204l7p5neiwgg">
          <Badge variant="info" className="text-sm px-3 py-1" data-id="element-2795" data-mp="203ihkzxwky1l">
            {pendingEncounters.length} Patients Waiting
          </Badge>
          <Badge variant="success" className="text-sm px-3 py-1 bg-green-100 text-green-800" data-id="element-2796" data-mp="1v8362endbe8c">
            {completedEncounters.length} Completed
          </Badge>
        </div>
      </div>

      <nav aria-label="Clinician services" data-id="element-2797" data-mp="er8nnhp2kdub">
        <ClinicianServiceCards activeTab={activeTab} onSelect={setActiveTab} data-id="element-2798" data-mp="es0l6y288ln8" />
      </nav>

      {activeTab === 'queue' && <Card noPadding data-id="element-2799" data-mp="2lg0uwebl8q1s">
          <ul className="divide-y divide-gray-200" data-id="element-2800" data-mp="2lgsjh56rpnik">
            {pendingEncounters.length > 0 ? pendingEncounters.map(encounter => {
          const patient = getPatient(encounter.patientId);
          if (!patient) return null;
          const isInvestigations = encounter.stage === 'INVESTIGATIONS';
          const hasResults = isInvestigations && !encounter.pendingLab && !encounter.pendingRadiology;
          const hasDraft = !!encounter.clinicalNote;
          const isPharmacy = encounter.stage === 'PHARMACY';
          const iconBg = hasResults ? 'bg-green-100' : isPharmacy ? 'bg-pink-100' : isInvestigations ? 'bg-yellow-100' : 'bg-blue-100';
          const iconColor = hasResults ? 'text-green-600' : isPharmacy ? 'text-pink-600' : isInvestigations ? 'text-yellow-600' : 'text-blue-600';
          return <li key={encounter.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-2801" data-mp="2tekt2nxilapp">
                    <div className="flex items-center" data-id="element-2802" data-mp="2kepa3wxpwbf4">
                      <div className={`p-2 rounded-full mr-4 ${iconBg}`} data-id="element-2803" data-mp="2kg8rxrz0uuj0">
                        <Stethoscope className={`h-6 w-6 ${iconColor}`} data-id="element-2804" data-mp="2fnpcepgrxpbk" />
                      </div>
                      <div data-id="element-2805" data-mp="2ao2z6g0y492r">
                        <p className="text-sm font-medium text-blue-600" data-id="element-2806" data-mp="2ape80u99070q">
                          {patient.bmcFileNumber}
                        </p>
                        <p className="text-lg font-bold text-gray-900" data-id="element-2807" data-mp="20r85cafmawut">
                          {patient.firstName} {patient.lastName}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-1" data-id="element-2808" data-mp="1wr7o6i14w7k2">
                          <ConsultationTypeBadge type={encounter.consultationType} size="sm" data-id="element-2809" data-mp="c6asschszdtt" />
                          <Badge variant="default" data-id="element-2810" data-mp="l7hagow6uuon">{patient.age} yrs</Badge>
                          {hasResults && <Badge variant="success" data-id="element-2811" data-mp="q5iouwio65ts">Results Ready</Badge>}
                          {isInvestigations && encounter.pendingLab && <Badge variant="warning" data-id="element-2812" data-mp="3tmsnbm1j2pnr">Awaiting Lab</Badge>}
                          {isInvestigations && encounter.pendingRadiology && <Badge variant="warning" data-id="element-2813" data-mp="77pr08mwkm39">Awaiting Radiology</Badge>}
                          {isPharmacy && <Badge variant="purple" data-id="element-2814" data-mp="35xgc6gmn47it">At Pharmacy</Badge>}
                          {hasDraft && encounter.stage === 'CLINICIAN' && <Badge variant="info" data-id="element-2815" data-mp="32vbnjt6mpb51">Draft Saved</Badge>}
                        </div>
                      </div>
                    </div>
                    <Button onClick={() => handleSelectEncounter(encounter)} data-id="element-2816" data-mp="3cmpps8pysp1v">
                      Open File
                    </Button>
                  </li>;
        }) : <li className="p-12 text-center text-gray-500" data-id="element-2817" data-mp="2jwqz68w4gjv3">
                <Stethoscope className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-2818" data-mp="2jzbzuukootr9" />
                <p className="text-lg font-medium text-gray-900" data-id="element-2819" data-mp="2fy09gmtzk4i0">
                  No patients waiting
                </p>
              </li>}
          </ul>
        </Card>}

      {activeTab === 'completed' && <Card data-id="element-2820" data-mp="957ji5pubq3k">
          <div className="mb-4" data-id="element-2821" data-mp="94y1p5ey8p8y">
            <input type="text" placeholder="Search completed consultations by patient name or file number..." className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-2822" data-mp="93wigdns4wg5" />
          </div>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-2823" data-mp="1d4cuotd53vsf">
            <ul className="divide-y divide-gray-200" data-id="element-2824" data-mp="1i1mm2ek3ahiw">
              {filteredCompleted.length > 0 ? filteredCompleted.map(encounter => {
            const patient = getPatient(encounter.patientId);
            if (!patient) return null;
            return <li key={encounter.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-2825" data-mp="1w14cszgy44uw">
                      <div className="flex items-center" data-id="element-2826" data-mp="2or4hoybg5hxr">
                        <div className="p-2 rounded-full mr-4 bg-green-100" data-id="element-2827" data-mp="2opjmr7l7zwzd">
                          <CheckCircle className="h-6 w-6 text-green-600" data-id="element-2828" data-mp="2ticu8382pjf5" />
                        </div>
                        <div data-id="element-2829" data-mp="tgwies616y2q">
                          <p className="text-sm font-medium text-blue-600" data-id="element-2830" data-mp="th4xmqpmmfh3">
                            {patient.bmcFileNumber}
                          </p>
                          <p className="text-lg font-bold text-gray-900" data-id="element-2831" data-mp="jqaa7j0n99u2">
                            {patient.firstName} {patient.lastName}
                          </p>
                          <div className="flex flex-wrap gap-2 mt-1 text-sm text-gray-500" data-id="element-2832" data-mp="1cepkxtcqt5zl">
                            <span data-id="element-2833" data-mp="17el3a003m57s">
                              {new Date(encounter.startedAt).toLocaleDateString()}
                            </span>
                            <span data-id="element-2834" data-mp="yf6gpbn1yesk">•</span>
                            <span data-id="element-2835" data-mp="yghi54t0zagi">
                              Attended by:{' '}
                              {encounter.lastAttendingClinician || 'Unknown'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button onClick={() => handleSelectEncounter(encounter)} data-id="element-2836" data-mp="1yo64zg50ljes">
                        Open File
                      </Button>
                    </li>;
          }) : <li className="p-12 text-center text-gray-500" data-id="element-2837" data-mp="27lgf2y63j2i4">
                  <p className="text-lg font-medium text-gray-900" data-id="element-2838" data-mp="2cerp1867fze9">
                    No completed consultations found
                  </p>
                </li>}
            </ul>
          </div>
        </Card>}
    </div>;
};